import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Msg, THOR, type TxResult } from "rujira.js";
import type { V2PrivacyProgress } from "rujira.ui";

import { useWalletAccounts } from "../../services/accounts";
import { privateActionNamesConnectedWallet } from "../../services/privateMessage";
import { useRedacted } from "../../services/redacted";
import type { V2Coin } from "./action";
import { PrivateAccountCatalog } from "./accountCatalog";
import {
  relayResubmittingOnPreflight,
  retryV2StaleRootOnce,
  V2_PREFLIGHT_RETRY_DELAY_MS,
  isV2RelayOutcomeUnknownError,
  isV2RelayRejectedError,
  REGISTRATION_SPONSOR_UNAVAILABLE,
  V2RelayOutcomeUnknownError,
  V2ChainClient,
  type V2ContractConfig,
  type V2RelayerMetadata,
  type V2ReserveTotals,
} from "./client";
import {
  deploymentConfig,
  isV2Enabled,
  loadProvingManifest,
  type V2DeploymentConfig,
} from "./config";
import { actionFeePolicy, type ActionFeePolicy } from "./feePolicy";
import {
  assertPrivateFeeTermsUnchanged,
  privateFeePolicy,
  privateIntent,
  quotePrivateFeeTerms,
  V2FeeQuoteBook,
  V2FeeReviewError,
  type V2FeeTerms,
  type V2PrivateFeeQuote,
  type V2PrivateIntent,
} from "./fees";
import { readPrivateFeeEstimate } from "./feeReads";
import {
  createRecoveryPackage,
  contextForRecoveryPackage,
  deriveKeysFromWallet,
  deriveKeysFromSeed,
  generateRandomKeys,
  openRecoveryPackage,
  type EncryptedRecoveryPackage,
  type V2KeyContext,
  type V2Keys,
} from "./keys";
import { MsgV2DirectExecute } from "./messages";
import { privateErrorMessage } from "./errors";
import {
  onboardingIdentity,
  hasPendingOnboardingNote,
  PendingOnboardingRegistry,
  waitForOnboardingNote,
  type PendingOnboarding,
} from "./onboarding";
import type { ControlNote, ReserveNote } from "./notes";
import {
  manifestProofGenerator,
  prepareControlRegistration,
  prepareCreateSubwallet,
  prepareReserveDeposit,
  prepareReserveMerge,
  prepareReserveToSubwallet,
  prepareReserveWithdraw,
  prepareShieldFromSubwallet,
  prepareSubwalletCall,
  type PreparedV2Execution,
  type ProofGenerator,
} from "./operations";
import { retryTransientRead } from "./readJson";
import {
  ownReserveChangedSince,
  recoverFromChain,
  type RecoveredNote,
} from "./recovery";
import { chooseRelayer } from "./relayerChoice";
import {
  loadPositions,
  savePositions,
  sectionOf,
  upsertPosition,
  type PositionEntry,
} from "./positionRegistry";
import { prefetchProvingArtifacts } from "./proofs";
import {
  DEFAULT_BLOCK_TIME_MS,
  DEFAULT_PREPARE_MS,
  DEFAULT_PROVE_MS,
  denomSymbol,
  describeCalls,
  expectedStepMs,
  formatCoins,
  mergesNeeded,
  nextProveEstimate,
  operationProgress,
  type OperationStepKey,
} from "./operationSteps";
import {
  classifySubwallets,
  displayedReserve,
  isModelBEnabled,
  nextInteractionSubwallet,
  planFunding,
  isOneShotIntent,
  pickReserveMerge,
  privateWalletMode,
  planSweep,
  sweepDelayMs,
  sweepDelayOverride,
  type FundingLeg,
  type PendingMove,
  type SubwalletInfo,
} from "./rotation";
import {
  assessReservePrivacyDetailed,
  unavailableReservePrivacy,
  type V2PrivacyInput,
} from "./privacy/indicator";
import { carriedLimits } from "./privacy/inheritance";
import {
  EMPTY_PRIVACY_STATE,
  type V2PrivacyAssessment,
  type V2PrivacyState,
} from "./privacy/types";
import { ensureRecoveryExportComplete } from "./safety";
import {
  earlierSpendingAccounts,
  findSpendingGeneration,
  firstFundingNotBefore,
  firstFundingPauseMs,
  mirrorGapMs,
  nextRegenerateHeight,
  RECENT_RETURN_MS,
  REGENERATE_COOLDOWN_MS,
  regenerateAvailability,
  spendingSalt,
  type FundingHold,
  type RecentReturn,
  type RegenerateAvailability,
  type SpendingGeneration,
} from "./spending";
import { createPrivateSessionVault } from "./sessionVault";
import { PrivateSessionIdle } from "./sessionIdle";
import {
  isPrivateSessionIdleMs,
  readPrivateSessionPolicy,
  writePrivateSessionPolicy,
  type PrivateSessionPolicy,
} from "./sessionPolicy";
import {
  readPrivateSessionIntent,
  writePrivateSessionIntent,
} from "./sessionIntent";

export type V2SessionStatus =
  | "disabled"
  | "locked"
  | "unlocking"
  | "needs-onboarding"
  | "onboarding"
  | "ready"
  | "processing"
  | "error";

type Material = {
  keys: V2Keys;
  keyContext: V2KeyContext;
  recovery: Awaited<ReturnType<typeof recoverFromChain>>;
  /**
   * When the account scan ran, and the newest Reserve totals read that had
   * finished before it (with a read after it, it brackets the scan).
   */
  scan?: {
    startedAt: number;
    finishedAt: number;
    totalsBefore?: V2ReserveTotals;
  };
  /** The newest new Spending account among the recovered subs, if any. */
  spending?: SpendingGeneration;
};

/** How long a confirmed private action waits for Undo before anything is sent. */
export const UNDO_WINDOW_MS = 3_000;

/**
 * A background return or merge stopped at a safe point (nothing of it was
 * proven or sent yet) because the user started an action. Not a failure.
 */
class V2BackgroundYield extends Error {
  constructor() {
    super("Background work paused for an action");
    this.name = "V2BackgroundYield";
  }
}

/** The user pressed Undo before the first transaction was sent. */
export class V2OperationCancelledError extends Error {
  readonly cancelled = true;
  constructor() {
    super("Cancelled. Nothing was sent.");
  }
}

type UndoWindow = {
  /** The action this window belongs to. */
  operationId: number;
  until: number;
  cancelled: boolean;
  /** Paused while the user hovers or focuses the toast. */
  heldSince?: number;
  /** Paused stretches; they do not count as progress. */
  pauses: [number, number | undefined][];
  wake?: () => void;
  /** Publishes the (possibly extended or paused) window to the toast. */
  onChange?: () => void;
  /** Marks the action cancelled as soon as Undo is accepted. */
  onCancel?: () => void;
};

export type V2PrivateOperationStep = {
  key: OperationStepKey;
  label: string;
  /** What the step does, e.g. the amount moved from the Private Reserve. */
  detail?: string;
  state: "done" | "active" | "todo";
  startedAt?: number;
  finishedAt?: number;
  /** One block plus proof generation, from measured averages. */
  expectedMs: number;
};

export type V2PrivateOperation = {
  id: number;
  label: string;
  /** Visible progress of a multi-transaction private action. */
  steps?: V2PrivateOperationStep[];
  /** When the step plan was first shown; the bar continues from `progressBase`. */
  planAt?: number;
  progressBase?: number;
  /** After a re-plan the time left shows again once it is no higher than this. */
  remainingCap?: number;
  /** Stretches the Undo countdown was paused; not counted as progress. */
  pauses?: readonly (readonly [number, number | undefined])[];
  /** Waiting for a previous transfer (a return or a merge) to finish. */
  waiting?: boolean;
  /** What it waits for: a new Spending account's first-funding pause. */
  waitingFor?: "spending";
  /** Average THORChain block time used for the estimates. */
  blockTimeMs?: number;
  finishedAt?: number;
  /** Until then nothing is sent and the user can undo the action. */
  undoUntil?: number;
  /** The undo countdown is paused (hover or focus). */
  undoHeld?: boolean;
  /** Undo time left while the countdown is paused. */
  undoLeftMs?: number;
  /** One short line for the toast: what happens right now. */
  statusText?: string;
  /** The user pressed Undo; nothing was sent. */
  cancelled?: boolean;
  phase: "preparing" | "submitting" | "pending" | "confirmed" | "failed";
  startedAt: number;
  txHash?: string;
  message?: string;
  checking?: boolean;
  finalTransaction?: boolean;
};

/** Model B state: one disposable sub per interaction plus automatic claw-back. */
export type V2ModelB = {
  enabled: boolean;
  /** Owned subs, oldest first. Never shown as separate accounts. */
  subwallets: readonly SubwalletInfo[];
  /** Sub of a position the user chose to manage; undefined = new interaction. */
  pinned?: string;
  /** Page where the pin was set; leaving that section starts fresh again. */
  pinnedPath?: string;
  /** Subs holding positions, with the product section that opened them. */
  positions: readonly PositionEntry[];
  /**
   * Subs whose positions the app shows, each once: the Spending sub first in
   * single mode (the active sub in rotate mode), then the position subs,
   * newest first. The order does not change while a position is managed.
   */
  scopes: readonly string[];
  /** Sum of open position values (8-decimal USD) across position subs. */
  positionsValueUsd: bigint;
  reportPositionValue(subwallet: string, valueUsd: bigint): void;
  /** A fresh one-time address is prepared for the next interaction. */
  poolReady: boolean;
  /** Protocol fee (bps) charged when funding a sub and again on its call. */
  feeBps: number;
  /** Settled balances still sitting in subs (returning to the Reserve). */
  subBalances: ReadonlyMap<string, readonly V2Coin[]>;
  /** Confirmed Reserve↔sub moves the published Reserve does not show yet. */
  pendingMoves: readonly PendingMove[];
  /** When the next claw-back runs (ms epoch), if one is scheduled. */
  nextSweepAt?: number;
  /** A background preparation, claw-back or Reserve merge is running. */
  background?: "preparing" | "returning" | "tidying";
  lastError?: string;
  pin(subwallet?: string): void;
  sweepNow(): void;
  setDustPolicy(isDust: (coin: Readonly<V2Coin>) => boolean): void;
  /**
   * Single mode: how many new Spending accounts this account created (0 =
   * still the first one). Undefined outside an unlocked single-mode account.
   */
  spendingGeneration?: number;
  /** Creating a new Spending account (see `regenerate`). */
  regeneration: V2SpendingRegeneration;
  /**
   * Single mode: create a new Spending account and use it for every new
   * action. The previous one keeps its open positions (still shown and
   * manageable); its free funds keep returning to the Private Reserve.
   */
  regenerate(): Promise<void>;
};

export type V2SpendingRegeneration = {
  running: boolean;
  /** Current step while running (sponsored registration, then creation). */
  phase?: V2PrivacyProgress["phase"];
  /** The next request is accepted from then on (ms epoch). */
  availableAt?: number;
  /** Why the last request failed. */
  error?: string;
};

type PendingPrivateRelay = {
  prepared: PreparedV2Execution;
  metadata: V2RelayerMetadata;
  operation: V2PrivateOperation;
  finalTransaction: boolean;
  checking: boolean;
};

export type V2Session = {
  enabled: boolean;
  proxyContract?: string;
  status: V2SessionStatus;
  isRefreshing: boolean;
  sessionExpired: boolean;
  privateSessionLocked: boolean;
  sessionPolicy: PrivateSessionPolicy;
  sessionPolicySaved: boolean;
  updateSessionPolicy(policy: PrivateSessionPolicy): void;
  dismissSessionExpired(): void;
  requestSessionUnlock(): void;
  assertSessionAccess(): void;
  error?: string;
  progress?: V2PrivacyProgress;
  operation?: V2PrivateOperation;
  dismissOperation(): void;
  /** Undo a just-confirmed action; true when nothing had been sent yet. */
  cancelOperation(operationId?: number): boolean;
  /** Pause (true) or resume (false) the undo countdown, e.g. on hover. */
  holdOperationUndo(hold: boolean, operationId?: number): void;
  checkOperation(): Promise<void>;
  recoveryExportRequired: boolean;
  backupRecommended: boolean;
  subwallets: string[];
  selectedSubwallet?: string;
  reserveBalances: ReadonlyMap<string, bigint>;
  accounts: ReturnType<PrivateAccountCatalog["list"]>;
  activeAccountId?: string;
  selectAccount(id: string, passphrase?: string): Promise<void>;
  renameAccount(id: string, label: string): void;
  removeAccount(id: string): void;
  activateWithWallet(): Promise<void>;
  continueWalletUnlock(): void;
  cancelWalletUnlock(): void;
  createRandomRecovery(passphrase: string): Promise<string>;
  confirmRecoveryExport(): void;
  importRecovery(serialized: string, passphrase: string): Promise<void>;
  exportRecovery(passphrase: string): Promise<string>;
  onboardCurrent(): Promise<void>;
  selectSubwallet(address: string): void;
  /** Change the visible account scope without discarding a live session. */
  showPrivate(): void;
  /** Change the visible account scope without discarding a live session. */
  showPublic(): void;
  lock(): void;
  refresh(): Promise<void>;
  directDepositMessage(value: V2Coin): Msg | null;
  quotePrivateFees(message: Msg): Promise<V2PrivateFeeQuote>;
  executePrivateIntent(
    message: Msg,
    reviewedQuote?: V2PrivateFeeQuote
  ): Promise<TxResult>;
  isV2DirectMessage(message: Msg): message is MsgV2DirectExecute;
  modelB: V2ModelB;
  /** Reserve privacy indicator per asset (computed locally, never sent). */
  privacy: V2PrivacyState;
};

const unavailable = async (): Promise<never> => {
  throw new Error("Private Mode is unavailable");
};

const Context = createContext<V2Session>({
  privacy: EMPTY_PRIVACY_STATE,
  enabled: false,
  status: "disabled",
  isRefreshing: false,
  sessionExpired: false,
  privateSessionLocked: false,
  sessionPolicy: { idleTimeoutMs: 900_000, alwaysPrivate: false },
  sessionPolicySaved: true,
  updateSessionPolicy: () => undefined,
  dismissSessionExpired: () => undefined,
  requestSessionUnlock: () => undefined,
  assertSessionAccess: () => undefined,
  recoveryExportRequired: false,
  backupRecommended: false,
  dismissOperation: () => undefined,
  cancelOperation: () => false,
  holdOperationUndo: () => undefined,
  checkOperation: async () => undefined,
  subwallets: [],
  reserveBalances: new Map(),
  accounts: [],
  selectAccount: unavailable,
  renameAccount: () => undefined,
  removeAccount: () => undefined,
  activateWithWallet: unavailable,
  continueWalletUnlock: () => undefined,
  cancelWalletUnlock: () => undefined,
  createRandomRecovery: unavailable,
  confirmRecoveryExport: () => undefined,
  importRecovery: unavailable,
  exportRecovery: unavailable,
  onboardCurrent: unavailable,
  selectSubwallet: () => undefined,
  showPrivate: () => undefined,
  showPublic: () => undefined,
  lock: () => undefined,
  refresh: unavailable,
  directDepositMessage: () => null,
  quotePrivateFees: unavailable,
  executePrivateIntent: unavailable,
  isV2DirectMessage: (message): message is MsgV2DirectExecute =>
    message instanceof MsgV2DirectExecute,
  modelB: {
    enabled: false,
    subwallets: [],
    poolReady: false,
    feeBps: 0,
    subBalances: new Map(),
    pendingMoves: [],
    positions: [],
    scopes: [],
    positionsValueUsd: 0n,
    reportPositionValue: () => undefined,
    pin: () => undefined,
    sweepNow: () => undefined,
    setDustPolicy: () => undefined,
    regeneration: { running: false },
    regenerate: unavailable,
  },
});

const recoveredSubwallets = (recovery: Material["recovery"]): SubwalletInfo[] =>
  classifySubwallets(
    recovery.activeNotes.filter(
      (note): note is RecoveredNote & { note: ControlNote } =>
        note.note.kind === "control"
    )
  );

const subwalletInfos = (material?: Material): SubwalletInfo[] =>
  material ? recoveredSubwallets(material.recovery) : [];

/**
 * The newest new Spending account among the recovered subs: its address
 * follows from a salt derived from the scan key, so every device finds the
 * same one (see spending.ts). Local computation only, no request; should it
 * fail, the account keeps today's rule (the most used sub).
 */
const detectSpending = (
  chain: V2ChainClient,
  keys: V2Keys,
  recovery: Material["recovery"]
): SpendingGeneration | undefined => {
  try {
    return findSpendingGeneration(
      recoveredSubwallets(recovery).map((info) => info.address),
      (generation) =>
        chain.predictSubwallet(spendingSalt(keys.scanKey, generation))
    );
  } catch {
    return undefined;
  }
};

/**
 * The sub of the next interaction for this material: in single mode its
 * Spending sub, or a pinned position account managing its own positions
 * (see nextInteractionSubwallet). Every new action uses the Spending sub.
 */
const interactionSubwallet = (
  material: Material | undefined,
  pinned: string | undefined,
  positions: readonly PositionEntry[]
): string | undefined =>
  material
    ? nextInteractionSubwallet(
        subwalletInfos(material),
        pinned,
        privateWalletMode(),
        {
          spending: material.spending?.address,
          positionAccounts: new Set(positions.map((entry) => entry.subwallet)),
        }
      )
    : undefined;

/** Single mode: how long after an action the Spending sub is checked every block. */
const SWEEP_WATCH_MS = 20 * 60_000;
/** Single mode: check interval outside the watch window. */
const SWEEP_IDLE_CHECK_MS = 60_000;
/** How long a preparatory step may take to land before the action stops. */
const INCLUSION_WAIT_MS = 150_000;
/** No new background work starts this long after a fee review opened. */
export const REVIEW_QUIET_MS = 45_000;

const sameSubBalances = (
  left: ReadonlyMap<string, readonly V2Coin[]>,
  right: ReadonlyMap<string, readonly V2Coin[]>
): boolean =>
  left.size === right.size &&
  [...right].every(([address, coins]) => {
    const other = left.get(address);
    return (
      !!other &&
      other.length === coins.length &&
      coins.every(
        (coin, index) =>
          other[index].denom === coin.denom &&
          other[index].amount === coin.amount
      )
    );
  });

/** The two smallest Reserve notes of the most fragmented denom, if any. */
const reserveMergePair = (material?: Material) =>
  pickReserveMerge(material?.recovery.activeNotes ?? []);

const DEFAULT_DUST: Record<string, bigint> = {
  // 0.01 RUNE: a claw-back of less is not worth its relayer gas.
  rune: 1_000_000n,
};
const defaultIsDust = (coin: Readonly<V2Coin>): boolean =>
  BigInt(coin.amount) < (DEFAULT_DUST[coin.denom] ?? 1_000n);

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Builds one proof and learns how long proofs take on this device. */
const measureProof = async <T,>(
  estimate: { current: number },
  build: () => Promise<T>
): Promise<T> => {
  const started = Date.now();
  const result = await build();
  estimate.current = nextProveEstimate(estimate.current, Date.now() - started);
  return result;
};

const activeControlNotes = (material: Material): RecoveredNote[] =>
  material.recovery.activeNotes
    .filter((note) => note.note.kind === "control")
    .sort((left, right) => right.index - left.index);

const copyKeys = (keys: V2Keys): V2Keys => ({
  ...keys,
  rootSeed: keys.rootSeed.slice(),
  scanKey: keys.scanKey.slice(),
});

const wipeKeys = (keys: V2Keys): void => {
  keys.rootSeed.fill(0);
  keys.scanKey.fill(0);
};

const reserveBalances = (material?: Material): Map<string, bigint> => {
  const result = new Map<string, bigint>();
  for (const recovered of material?.recovery.activeNotes ?? []) {
    if (recovered.note.kind !== "reserve") continue;
    result.set(
      recovered.note.denom,
      (result.get(recovered.note.denom) ?? 0n) + BigInt(recovered.note.amount)
    );
  }
  return result;
};

/** The later of the daily limit and the pause after the last request. */
const regenerationAvailableAt = (
  settledAt: number | undefined,
  dailyAt: number | undefined
): number | undefined => {
  const at = Math.max(
    settledAt === undefined ? 0 : settledAt + REGENERATE_COOLDOWN_MS,
    dailyAt ?? 0
  );
  return at > 0 ? at : undefined;
};

/** "5 h", "12 min" or "40 s": how long until a limit opens. */
const waitText = (ms: number): string =>
  ms >= 3_600_000
    ? `${Math.ceil(ms / 3_600_000)} h`
    : ms >= 60_000
      ? `${Math.ceil(ms / 60_000)} min`
      : `${Math.max(1, Math.ceil(ms / 1_000))} s`;

/** Why a new Spending account cannot be created right now. */
const regenerateRefusal = (
  availability: RegenerateAvailability,
  now = Date.now()
): string => {
  if (availability.allowed) return "";
  switch (availability.reason) {
    case "running":
      return "A new Spending account is already being created.";
    case "busy":
      return "Wait until your current transaction is done, then try again.";
    case "daily":
      return `You can create one new Spending account a day. The next one is possible in about ${waitText(
        (availability.retryAt ?? now) - now
      )}.`;
    case "cooldown":
      return `You can create another Spending account in ${Math.max(
        1,
        Math.ceil(((availability.retryAt ?? now) - now) / 1_000)
      )} s.`;
    default:
      return "Unlock your private account to create a new Spending account.";
  }
};

/** Wait before a privacy assessment: publish bursts and balance ticks settle. */
const PRIVACY_ASSESS_DELAY_MS = 800;
/** A Reserve totals read older than this no longer brackets a new scan. */
const PRIVACY_BRACKET_MAX_AGE_MS = 60_000;
/** Missing pool data is read again for one scan after these pauses. */
const PRIVACY_RETRY_DELAYS_MS = [8_000, 16_000, 32_000, 60_000, 120_000];
/** How long a good label may stand in for a check that lacks pool data. */
const PRIVACY_KEEP_MS = 10 * 60_000;

/** The user's unspent Reserve notes per asset, as comparable text. */
const ownNotesByDenom = (
  notes: V2PrivacyInput["notes"]
): Map<string, string> => {
  const byDenom = new Map<string, string[]>();
  for (const note of notes)
    if (!note.spent && note.amount > 0n)
      byDenom.set(note.denom, [
        ...(byDenom.get(note.denom) ?? []),
        note.commitment ?? "",
      ]);
  return new Map(
    [...byDenom].map(([denom, list]) => [denom, list.sort().join(",")])
  );
};

/** Public pool data read after one account scan (privacy indicator). */
type PrivacyPoolData = {
  before?: V2ReserveTotals;
  after: V2ReserveTotals;
  /** A tree read after `after` still matched the scanned tree. */
  unchangedSinceScan: boolean;
  /** The pool moved after the scan, but none of the user's Reserve notes. */
  ownUnchangedSinceScan: boolean;
  /** When this pool data was confirmed (ms since epoch). */
  checkedAt: number;
};

const privacyUnits = (amount: string): bigint =>
  /^\d{1,78}$/.test(amount) ? BigInt(amount) : 0n;

/**
 * Inputs of the local privacy assessment. Only the user's own subs count as
 * Spending; `carried` holds the commitments of notes that keep a limit from
 * the user's own moves, `moves` the Reserve moves the scan does not show yet.
 * Nothing of this leaves the browser.
 */
const privacyInput = (
  current: Material,
  balances: ReadonlyMap<string, readonly V2Coin[]>,
  pool: PrivacyPoolData | undefined,
  isDust: (coin: Readonly<V2Coin>) => boolean,
  carried: ReadonlySet<string> = new Set(),
  moves: readonly PendingMove[] = []
): V2PrivacyInput => {
  const owned = new Set(subwalletInfos(current).map((info) => info.address));
  const recovered = current.recovery.notes ?? current.recovery.activeNotes;
  const spending = [...balances].flatMap(([address, coins]) =>
    owned.has(address) ? coins : []
  );
  // A return already sent counts once its coin has left Spending, the same
  // way the balance shows it, until a scan shows its note.
  const returns = moves.filter((move) => move.direction === "return");
  const returned = [...new Set(returns.map((move) => move.denom))].map(
    (denom) => ({
      denom,
      amount: displayedReserve(
        denom,
        0n,
        spending
          .filter((coin) => coin.denom === denom)
          .reduce((sum, coin) => sum + privacyUnits(coin.amount), 0n),
        returns
      ),
    })
  );
  return {
    notes: recovered.flatMap((entry) =>
      entry.note.kind === "reserve"
        ? [
            {
              denom: entry.note.denom,
              amount: privacyUnits(entry.note.amount),
              encryptedLength: entry.encryptedLength,
              spent: entry.spent === true,
              commitment: entry.note.commitment,
              inheritsLimit: carried.has(entry.note.commitment),
            },
          ]
        : []
    ),
    activeNoteCount: current.recovery.activeNotes.length,
    pool: current.recovery.pool,
    totalsBefore: pool?.before,
    totalsAfter: pool?.after,
    unchangedSinceScan: pool?.unchangedSinceScan,
    ownUnchangedSinceScan: pool?.ownUnchangedSinceScan,
    spending,
    returned,
    isDust,
  };
};

const strictRecoveryPackage = (
  serialized: string
): EncryptedRecoveryPackage => {
  let value: Partial<EncryptedRecoveryPackage>;
  try {
    value = JSON.parse(serialized);
  } catch {
    throw new Error(
      "Could not read this recovery backup. Choose its original JSON file and try again."
    );
  }
  if (
    !value ||
    typeof value !== "object" ||
    value.version !== 2 ||
    typeof value.walletAddress !== "string" ||
    value.kdf !== "PBKDF2-SHA256" ||
    value.iterations !== 600_000 ||
    typeof value.salt !== "string" ||
    typeof value.nonce !== "string" ||
    typeof value.ciphertext !== "string" ||
    typeof value.contextHash !== "string"
  ) {
    throw new Error("Invalid Private Mode recovery package");
  }
  return value as EncryptedRecoveryPackage;
};

export const RedactedV2Context: FC<PropsWithChildren> = ({ children }) => {
  const enabled = isV2Enabled();
  const accounts = useWalletAccounts();
  const connectedAddresses = useRef<string[]>([]);
  connectedAddresses.current = (accounts.accounts ?? []).map(
    (account) => account.address.address
  );
  const redacted = useRedacted();
  const [status, setStatus] = useState<V2SessionStatus>(
    enabled ? "locked" : "disabled"
  );
  const [error, setError] = useState<string>();
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionPolicy, setSessionPolicy] = useState(readPrivateSessionPolicy);
  const sessionPolicyRef = useRef(sessionPolicy);
  sessionPolicyRef.current = sessionPolicy;
  const [sessionPolicySaved, setSessionPolicySaved] = useState(true);
  const privateSessionIntent = useRef(
    enabled && (readPrivateSessionIntent() || sessionPolicy.alwaysPrivate)
  );
  const idle = useRef(new PrivateSessionIdle(sessionPolicy.idleTimeoutMs));
  const vault = useRef<ReturnType<typeof createPrivateSessionVault>>();
  const cachedIdentity = useRef<string>();
  const cacheReady = useRef(false);
  const previousWallet = useRef<string>();
  const previousAddress = useRef<string>();
  // Only public receipt identity survives idle expiry; no spending material.
  const expiredOperation = useRef<{ generation: number; identity: string }>();
  const [progress, setProgress] = useState<V2PrivacyProgress>();
  const [operation, setOperation] = useState<V2PrivateOperation>();
  const operationSequence = useRef(0);
  const [revision, setRevision] = useState(0);
  const refreshSequence = useRef(0);
  const [refreshScope, setRefreshScope] = useState<{
    id: number;
    generation: number;
    accountId: string;
  }>();
  const [recoveryExportRequired, setRecoveryExportRequired] = useState(false);
  const [selectedSubwallet, setSelectedSubwallet] = useState<string>();
  // Model B: one disposable sub per interaction, automatic claw-back.
  const [modelBEnabled] = useState(() => enabled && isModelBEnabled());
  const [pinnedSubwallet, setPinnedSubwallet] = useState<string>();
  const [pinnedPath, setPinnedPath] = useState<string>();
  const [positions, setPositions] = useState<readonly PositionEntry[]>([]);
  const [feeBps, setFeeBps] = useState(0);
  const [positionValues, setPositionValues] = useState<
    ReadonlyMap<string, bigint>
  >(new Map());
  const reportPositionValue = useCallback(
    (subwallet: string, valueUsd: bigint) =>
      setPositionValues((current) => {
        if (current.get(subwallet) === valueUsd) return current;
        const next = new Map(current);
        next.set(subwallet, valueUsd);
        return next;
      }),
    []
  );
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const positionsIdentity = useRef<string>();
  // The identity whose sealed position list has finished loading.
  const positionsLoaded = useRef<string>();
  // A new Spending account on request (single mode, see spending.ts). The
  // limit is kept in memory for this device: one at a time, then a pause.
  const [regeneration, setRegeneration] = useState<V2SpendingRegeneration>({
    running: false,
  });
  const regenerating = useRef(false);
  const regenerateSettledAt = useRef<number>();
  // One a day, counted in blocks from the current Spending account's creation
  // (chain data, the same on every device); converted to a time for the UI.
  const dailyAvailableAt = useRef<number>();
  // Privacy of the switch (memory only): the new account's first funding
  // waits for a random pause, and for a gap after a return it would mirror.
  const fundingHold = useRef<FundingHold>();
  const recentReturns = useRef<RecentReturn[]>([]);
  const pinnedRef = useRef<string>();
  pinnedRef.current = pinnedSubwallet;
  const [subBalances, setSubBalances] = useState<
    ReadonlyMap<string, readonly V2Coin[]>
  >(new Map());
  const subBalancesRef = useRef(subBalances);
  subBalancesRef.current = subBalances;
  const [background, setBackground] = useState<
    "preparing" | "returning" | "tidying"
  >();
  // The running background job (pool refill or claw-back). Foreground actions
  // wait for it instead of failing with "already running".
  const backgroundJob = useRef<Promise<void>>();
  // Set by an action that wants the running background job to stop at its
  // next safe point (before it proves or sends anything).
  const backgroundYield = useRef<{ requested: boolean }>();
  // While a fee review is open, background work waits: the confirmation
  // should not have to wait for a return or merge started meanwhile.
  const reviewQuietUntil = useRef(0);
  const [modelBError, setModelBError] = useState<string>();
  const [nextSweepAt, setNextSweepAt] = useState<number>();
  const nextSweepAtRef = useRef<number>();
  const dustPolicy = useRef(defaultIsDust);
  const nextConsolidateAt = useRef(0);
  // Single mode watches the Spending sub block by block after an action, so a
  // payout returns to the Reserve as soon as it lands.
  const watchUntil = useRef(0);
  const sweepCheck = useRef(false);
  const [pendingMoves, setPendingMoves] = useState<readonly PendingMove[]>([]);
  const pendingMovesRef = useRef(pendingMoves);
  pendingMovesRef.current = pendingMoves;
  // Reserve privacy indicator: computed locally from the account scan and
  // public reads; kept in memory only, never logged, stored or sent.
  const [privacy, setPrivacy] = useState<V2PrivacyState>(EMPTY_PRIVACY_STATE);
  // Newest Reserve totals read: it brackets the next account scan.
  const poolTotals = useRef<V2ReserveTotals>();
  // Pool data read for the last assessed scan (reused for balance changes).
  const privacyPool = useRef<{
    material: Material;
    data: Promise<PrivacyPoolData>;
  }>();
  const privacyRun = useRef(0);
  // Re-reads of missing pool data for one scan (see PRIVACY_RETRY_DELAYS_MS).
  const privacyRetry = useRef<{
    material?: Material;
    count: number;
    timer?: ReturnType<typeof setTimeout>;
  }>({ count: 0 });
  const [privacyRetryTick, setPrivacyRetryTick] = useState(0);
  const privacyIdentity = useRef<string>();
  // Notes the user's own merge, change or return made from limited funds
  // (commitments): they keep that limit. The labels last shown, per asset and
  // per note, decide what a new move carries over. Memory only; cleared with
  // the labels.
  const privacyCarried = useRef(new Set<string>());
  const privacyShown = useRef<{
    byDenom: ReadonlyMap<string, V2PrivacyAssessment>;
    limitedNotes: ReadonlySet<string>;
    /** Whose labels, when, and the user's unspent Reserve notes per asset. */
    identity?: string;
    at?: number;
    notes?: ReadonlyMap<string, string>;
  }>();
  // Open while a confirmed action may still be undone (nothing sent yet).
  const undoWindow = useRef<UndoWindow>();
  // Measured on this device: step estimates in the progress toast.
  const blockTimeMs = useRef(DEFAULT_BLOCK_TIME_MS);
  const blockTimeReadAt = useRef(0);
  const proveMs = useRef(DEFAULT_PROVE_MS);
  const prepareMs = useRef(DEFAULT_PREPARE_MS);
  // Whether the relayer bundled funding with the action when fees were last
  // read: the step plan is known before the first step is shown.
  const fundingBundles = useRef(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  const isPrivateModeRef = useRef(redacted.isPrivateMode);
  isPrivateModeRef.current = redacted.isPrivateMode;
  const material = useRef<Material>();
  const pendingOnboarding = useRef<PendingOnboarding>();
  // Submission receipts contain no spending keys. Keep them per identity so
  // returning to another account cannot turn an ambiguous send into a retry.
  const pendingByIdentity = useRef(new PendingOnboardingRegistry());
  // Public submission receipts only, never decrypted notes or spending keys.
  // Kept in memory across lock so a lost HTTP response cannot enable a new send.
  const pendingRelays = useRef(new Map<string, PendingPrivateRelay>());
  const pendingRecoveryBackups = useRef(new Map<string, string>());
  const busy = useRef(false);
  const sessionGeneration = useRef(0);
  // Only a continuation gate lives here, never a signature or derived key.
  const walletSignatureContinuation = useRef<{
    generation: number;
    resolve(): void;
    reject(error: Error): void;
  }>();
  const feeQuotes = useRef(new V2FeeQuoteBook());
  const feeIdentityEpoch = useRef(0);
  const currentFeeSelection = useRef({ wallet: "", subwallet: "" });
  const deployment = useRef<V2DeploymentConfig>();
  const chain = useRef<V2ChainClient>();
  const recoveryExportGate = useRef(false);
  const catalog = useRef<PrivateAccountCatalog>();

  const reportProgress = useCallback(
    (generation: number, next?: V2PrivacyProgress) => {
      if (generation === sessionGeneration.current) setProgress(next);
    },
    []
  );

  const setRecoveryExportGate = useCallback((required: boolean) => {
    recoveryExportGate.current = required;
    setRecoveryExportRequired(required);
  }, []);

  const selected = accounts.selected;
  const walletAddress = selected?.address.address;
  const providerId = selected?.provider;
  currentFeeSelection.current = {
    wallet: walletAddress ?? "",
    subwallet: selectedSubwallet ?? "",
  };

  const services = useCallback((): {
    deployment: V2DeploymentConfig;
    chain: V2ChainClient;
  } => {
    deployment.current ??= deploymentConfig();
    chain.current ??= new V2ChainClient(deployment.current);
    return { deployment: deployment.current, chain: chain.current };
  }, []);

  const accountCatalog = useCallback(() => {
    if (!catalog.current) {
      const config = services().deployment;
      let storage: Storage | undefined;
      try {
        storage = window.localStorage;
      } catch {
        /* Session-only storage. */
      }
      catalog.current = new PrivateAccountCatalog(
        {
          chainId: config.chainId,
          proxyAddress: config.proxyContract,
        },
        storage
      );
    }
    return catalog.current;
  }, [services]);

  const wipeMaterial = useCallback(
    (preserveReceipt = false) => {
      sessionGeneration.current += 1;
      setRefreshScope(undefined);
      walletSignatureContinuation.current?.reject(
        new Error("Private Mode changed while unlocking")
      );
      walletSignatureContinuation.current = undefined;
      pendingOnboarding.current = undefined;
      pendingRecoveryBackups.current.clear();
      catalog.current?.lock();
      setProgress(undefined);
      if (!preserveReceipt) setOperation(undefined);
      if (material.current) {
        wipeKeys(material.current.keys);
      }
      material.current = undefined;
      // The privacy indicator never outlives the session it describes.
      privacyRun.current += 1;
      privacyPool.current = undefined;
      privacyIdentity.current = undefined;
      privacyCarried.current = new Set();
      privacyShown.current = undefined;
      setPrivacy(EMPTY_PRIVACY_STATE);
      fundingHold.current = undefined;
      recentReturns.current = [];
      dailyAvailableAt.current = undefined;
      setRecoveryExportGate(false);
      if (!preserveReceipt) setSelectedSubwallet(undefined);
      setRevision((value) => value + 1);
    },
    [setRecoveryExportGate]
  );

  // Undo is accepted for as long as nothing was sent: the window stays current
  // until the moment the first transaction goes out, even past its deadline
  // (a click queued behind proof work still counts).
  const cancelOperation = useCallback((operationId?: number): boolean => {
    const window = undoWindow.current;
    if (
      !window ||
      window.cancelled ||
      (operationId !== undefined && window.operationId !== operationId)
    )
      return false;
    window.cancelled = true;
    const now = Date.now();
    if (window.heldSince !== undefined) {
      window.pauses = window.pauses.map(([from, to]) => [from, to ?? now]);
      window.heldSince = undefined;
    }
    window.onCancel?.();
    window.wake?.();
    return true;
  }, []);

  const holdOperationUndo = useCallback(
    (hold: boolean, operationId?: number) => {
      const window = undoWindow.current;
      if (
        !window ||
        window.cancelled ||
        (operationId !== undefined && window.operationId !== operationId)
      )
        return;
      const now = Date.now();
      if (hold && window.heldSince === undefined) {
        if (now >= window.until) return;
        window.heldSince = now;
        window.pauses = [...window.pauses, [now, undefined]];
      } else if (!hold && window.heldSince !== undefined) {
        // The countdown resumes where it stopped.
        window.until += now - window.heldSince;
        window.heldSince = undefined;
        window.pauses = window.pauses.map(([from, to]) => [from, to ?? now]);
      } else return;
      window.onChange?.();
      window.wake?.();
    },
    []
  );

  const dismissOperation = useCallback(() => {
    setOperation((current) =>
      current && ["confirmed", "failed"].includes(current.phase)
        ? undefined
        : current
    );
  }, []);

  const lock = useCallback(() => {
    void vault.current?.clear();
    cacheReady.current = false;
    cachedIdentity.current = undefined;
    idle.current.clear();
    privateSessionIntent.current = false;
    expiredOperation.current = undefined;
    writePrivateSessionIntent(false);
    setSessionExpired(false);
    wipeMaterial();
    setError(undefined);
    setStatus(enabled ? "locked" : "disabled");
    if (enabled) {
      redacted.setIsPrivateMode(false);
      redacted.setStealth("");
      redacted.setSubWalletAddress("");
      redacted.setPublicAddress("");
    }
  }, [enabled, redacted, wipeMaterial]);

  const expireSession = useCallback(() => {
    if (!privateSessionIntent.current) return;
    const current = material.current;
    if (current)
      expiredOperation.current = {
        generation: sessionGeneration.current,
        identity: onboardingIdentity(current.keys, current.keyContext),
      };
    void vault.current?.clear();
    cacheReady.current = false;
    cachedIdentity.current = undefined;
    idle.current.clear();
    wipeMaterial(true);
    redacted.setStealth("");
    redacted.setIsPrivateMode(true);
    setError(undefined);
    setStatus("locked");
    setSessionExpired(true);
  }, [redacted, wipeMaterial]);
  const expireRef = useRef(expireSession);
  expireRef.current = expireSession;
  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);
  const requestSessionUnlock = useCallback(() => setSessionExpired(true), []);
  const assertSessionAccess = useCallback(() => {
    if (!privateSessionIntent.current) return;
    if (!material.current || !idle.current.check()) {
      if (material.current) expireRef.current();
      else setSessionExpired(true);
      throw new Error("Unlock Private Mode to continue.");
    }
  }, []);

  // Account selection is also a view switch. Do not turn that ordinary action
  // into a logout: a live private session remains protected by the existing
  // idle timer and is still cleared on an actual wallet/provider change.
  const showPublic = useCallback(() => {
    if (!enabled) return;
    redacted.setIsPrivateMode(false);
    redacted.updateRedactedFlagData?.();
  }, [enabled, redacted]);

  const showPrivate = useCallback(() => {
    if (!enabled) return;
    if (
      !privateSessionIntent.current ||
      !material.current ||
      !idle.current.check()
    ) {
      if (material.current) expireRef.current();
      else {
        redacted.setIsPrivateMode(true);
        setSessionExpired(true);
      }
      return;
    }
    redacted.setIsPrivateMode(true);
    redacted.updateRedactedFlagData?.();
  }, [enabled, redacted]);

  const prove = useCallback(async (): Promise<ProofGenerator> => {
    const { deployment, chain } = services();
    await chain.attestProxyDeployment();
    return manifestProofGenerator(await loadProvingManifest(deployment));
  }, [services]);

  const recover = useCallback(
    async (keys: V2Keys, keyContext: V2KeyContext): Promise<Material> => {
      const { chain } = services();
      // The newest Reserve totals read, finished before this scan starts,
      // brackets it for the privacy indicator (no request here).
      const startedAt = Date.now();
      const previous = poolTotals.current;
      const totalsBefore =
        previous &&
        startedAt - previous.finishedAt <= PRIVACY_BRACKET_MAX_AGE_MS
          ? previous
          : undefined;
      const recovery = await recoverFromChain({
        keys,
        context: keyContext,
        source: chain.recoverySource,
      });
      return {
        keys,
        keyContext,
        recovery,
        scan: { startedAt, finishedAt: Date.now(), totalsBefore },
        spending: detectSpending(chain, keys, recovery),
      };
    },
    [services]
  );

  const publish = useCallback(
    (next: Material, privateMode: boolean) => {
      // A fresh recovery already contains every confirmed Reserve move.
      setPendingMoves((moves) => (moves.length ? [] : moves));
      const previous = material.current;
      // Recovery refreshes copy key buffers without changing ownership. Only
      // an actual identity replacement invalidates its pending fee reviews.
      if (
        !previous ||
        previous.keys.ownerKey !== next.keys.ownerKey ||
        JSON.stringify(previous.keyContext) !== JSON.stringify(next.keyContext)
      ) {
        feeIdentityEpoch.current += 1;
        setOperation(
          pendingRelays.current.get(
            onboardingIdentity(next.keys, next.keyContext)
          )?.operation
        );
        // Another account's privacy labels are never shown for this one.
        if (previous) {
          privacyRun.current += 1;
          privacyPool.current = undefined;
          privacyIdentity.current = undefined;
          privacyCarried.current = new Set();
          privacyShown.current = undefined;
          setPrivacy(EMPTY_PRIVACY_STATE);
        }
      }
      const subwallets = activeControlNotes(next)
        .map((note) => (note.note as ControlNote).subwallet)
        .filter((value): value is string => !!value);
      // Model B: the active scope is the managed position's sub, else the
      // oldest fresh one-time sub. A used sub is only a fallback until the
      // background pool has prepared a fresh one.
      const preferred =
        (modelBEnabled &&
          interactionSubwallet(
            next,
            pinnedRef.current,
            positionsRef.current
          )) ||
        (selectedSubwallet &&
          subwallets.includes(selectedSubwallet) &&
          selectedSubwallet) ||
        subwallets[0];
      if (privateMode && preferred && !recoveryExportGate.current) {
        const identity = onboardingIdentity(next.keys, next.keyContext);
        if (cachedIdentity.current === identity) {
          if (!idle.current.check()) {
            expireRef.current();
            throw new Error("Unlock Private Mode to continue.");
          }
        } else {
          const now = Date.now();
          idle.current.start(now);
          cachedIdentity.current = identity;
          cacheReady.current = false;
          const generation = sessionGeneration.current;
          const currentVault = vault.current;
          // save copies the seed synchronously; no raw signature is retained.
          void currentVault
            ?.save(next.keys.rootSeed, next.keyContext, now)
            .then((saved) => {
              if (
                generation !== sessionGeneration.current ||
                vault.current !== currentVault
              )
                return;
              cacheReady.current = saved;
            });
        }
        privateSessionIntent.current = true;
        writePrivateSessionIntent(true);
        expiredOperation.current = undefined;
        setSessionExpired(false);
      }
      accountCatalog().remember(next.keys, next.keyContext, {
        subwallet: preferred,
      });
      material.current = next;
      pendingOnboarding.current = pendingByIdentity.current.get(
        onboardingIdentity(next.keys, next.keyContext)
      );
      if (previous && previous.keys !== next.keys) wipeKeys(previous.keys);
      setSelectedSubwallet(preferred);
      setRevision((value) => value + 1);
      redacted.setPublicAddress(next.keyContext.walletAddress);
      redacted.setStealth(next.keys.ownerKey);
      redacted.setSubWalletAddress(preferred ?? "");
      redacted.setIsPrivateMode(privateMode && !!preferred);
      // A recovered or just-confirmed private account has a new balance scope.
      // Re-run account queries immediately instead of waiting for the private
      // polling interval or a page reload.
      redacted.updateRedactedFlagData?.();
      setStatus(preferred ? "ready" : "needs-onboarding");
      setError(undefined);
    },
    [accountCatalog, modelBEnabled, redacted, selectedSubwallet]
  );

  // Latest callbacks avoid treating ordinary account/recovery renders as a
  // wallet change. Restore only after the actual provider and wallet arrive.
  const lifecycle = useRef({
    lock,
    publish,
    recover,
    showLocked: () => redacted.setIsPrivateMode(true),
  });
  lifecycle.current = {
    lock,
    publish,
    recover,
    showLocked: () => redacted.setIsPrivateMode(true),
  };
  useEffect(() => {
    if (!enabled) return;
    const bindingId =
      walletAddress && providerId
        ? `${String(providerId)}:${walletAddress}`
        : undefined;
    if (
      (previousWallet.current && previousWallet.current !== bindingId) ||
      (previousAddress.current && previousAddress.current !== walletAddress)
    ) {
      lifecycle.current.lock();
      if (sessionPolicyRef.current.alwaysPrivate && bindingId) {
        // A wallet change revokes the previous keys, but an opted-in private
        // entry must not silently expose a public transaction form.
        privateSessionIntent.current = true;
        writePrivateSessionIntent(true);
        lifecycle.current.showLocked();
      }
    }
    previousWallet.current = bindingId;
    previousAddress.current = walletAddress;
    if (walletAddress && providerId) {
      const config = services().deployment;
      vault.current = createPrivateSessionVault(
        {
          chainId: config.chainId,
          proxyAddress: config.proxyContract,
          walletAddress,
          providerId: String(providerId),
        },
        { idleTimeoutMs: sessionPolicyRef.current.idleTimeoutMs }
      );
    } else vault.current = undefined;
    const generation = sessionGeneration.current;
    const currentVault = vault.current;
    if (privateSessionIntent.current && currentVault) {
      setStatus("unlocking");
      void (async () => {
        let keys: V2Keys | undefined;
        try {
          await services().chain.attestProxyDeployment();
          if (generation !== sessionGeneration.current) return;
          const saved = await currentVault.load();
          if (saved.status !== "restored") {
            if (generation === sessionGeneration.current) expireRef.current();
            return;
          }
          try {
            if (generation !== sessionGeneration.current) return;
            if (
              !idle.current.start(
                saved.lastActivity,
                Date.now(),
                saved.expiresAt
              )
            ) {
              expireRef.current();
              return;
            }
            keys = await deriveKeysFromSeed(saved.rootSeed, saved.keyContext);
          } finally {
            saved.rootSeed.fill(0);
          }
          if (generation !== sessionGeneration.current) return;
          const next = await lifecycle.current.recover(keys, saved.keyContext);
          if (generation !== sessionGeneration.current) return;
          if (
            !idle.current.check() ||
            !activeControlNotes(next).some(
              (note) => !!(note.note as ControlNote).subwallet
            )
          ) {
            expireRef.current();
            return;
          }
          // Refresh recovery is read-only: never register or create an account.
          cachedIdentity.current = onboardingIdentity(keys, saved.keyContext);
          cacheReady.current = true;
          lifecycle.current.publish(next, true);
        } catch {
          if (generation === sessionGeneration.current) expireRef.current();
        } finally {
          if (keys && material.current?.keys !== keys) wipeKeys(keys);
        }
      })();
    }
    return () => {
      // Unmount/refresh invalidates async work and memory, not the valid cache.
      sessionGeneration.current += 1;
      walletSignatureContinuation.current?.reject(
        new Error("Private Mode changed while unlocking")
      );
      walletSignatureContinuation.current = undefined;
      catalog.current?.lock();
      pendingRecoveryBackups.current.clear();
      if (material.current) wipeKeys(material.current.keys);
      material.current = undefined;
      cacheReady.current = false;
    };
  }, [enabled, walletAddress, providerId, services]);

  const updateSessionPolicy = useCallback(
    (policy: PrivateSessionPolicy) => {
      if (
        !isPrivateSessionIdleMs(policy.idleTimeoutMs) ||
        typeof policy.alwaysPrivate !== "boolean" ||
        (policy.undoBeforeSending !== undefined &&
          typeof policy.undoBeforeSending !== "boolean")
      )
        throw new Error("Invalid private session settings");
      sessionPolicyRef.current = { ...policy };
      setSessionPolicy({ ...policy });
      setSessionPolicySaved(writePrivateSessionPolicy(policy));
      const active = idle.current.setIdleTimeoutMs(policy.idleTimeoutMs);
      if (walletAddress && providerId) {
        const config = services().deployment;
        // Changing a preference does not renew a dormant authenticated session.
        vault.current = createPrivateSessionVault(
          {
            chainId: config.chainId,
            proxyAddress: config.proxyContract,
            walletAddress,
            providerId: String(providerId),
          },
          { idleTimeoutMs: policy.idleTimeoutMs }
        );
      }
      if (material.current && !active) expireRef.current();
      // This changes the next-visit preference only; no automatic signature.
    },
    [providerId, services, walletAddress]
  );

  useEffect(() => {
    if (!enabled) return;
    let touchTimer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      if (
        privateSessionIntent.current &&
        material.current &&
        cachedIdentity.current &&
        !idle.current.check()
      )
        expireRef.current();
    };
    const activity = (event: Event) => {
      if (
        !event.isTrusted ||
        document.visibilityState === "hidden" ||
        !material.current ||
        !cachedIdentity.current ||
        !privateSessionIntent.current
      )
        return;
      if (!idle.current.activity()) {
        expireRef.current();
        return;
      }
      // Batch bursts of deliberate input, not price polling or React renders.
      if (touchTimer) return;
      touchTimer = setTimeout(() => {
        touchTimer = undefined;
        const currentVault = vault.current;
        const lastActivity = idle.current.lastActivity;
        const generation = sessionGeneration.current;
        if (!currentVault || !cacheReady.current || lastActivity === undefined)
          return;
        void currentVault.touch(lastActivity).then((result) => {
          if (
            generation === sessionGeneration.current &&
            result.status !== "active"
          )
            expireRef.current();
        });
      }, 250);
    };
    const inputs = ["pointerdown", "keydown", "touchstart", "wheel"];
    inputs.forEach((type) =>
      document.addEventListener(type, activity, { passive: true })
    );
    document.addEventListener("visibilitychange", check);
    window.addEventListener("pageshow", check);
    window.addEventListener("focus", check);
    const timer = setInterval(check, 1000);
    return () => {
      inputs.forEach((type) => document.removeEventListener(type, activity));
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("focus", check);
      clearInterval(timer);
      if (touchTimer) clearTimeout(touchTimer);
    };
  }, [enabled]);

  const refresh = useCallback(async () => {
    assertSessionAccess();
    const current = material.current;
    if (!current) throw new Error("Unlock Private Mode first");
    const generation = sessionGeneration.current;
    const operationKeys = copyKeys(current.keys);
    const refreshId = ++refreshSequence.current;
    setRefreshScope({
      id: refreshId,
      generation,
      accountId: current.keys.recoveryId,
    });
    let next: Material | undefined;
    try {
      next = await recover(operationKeys, current.keyContext);
      if (
        generation !== sessionGeneration.current ||
        material.current !== current
      ) {
        throw new Error("Private Mode changed while refreshing");
      }
      publish(next, redacted.isPrivateMode);
    } finally {
      if (!next || material.current !== next) wipeKeys(operationKeys);
      setRefreshScope((scope) => (scope?.id === refreshId ? undefined : scope));
    }
  }, [assertSessionAccess, publish, recover, redacted.isPrivateMode]);

  const resumeRefresh = useRef({
    refresh,
    busy: false,
    lastStarted: 0,
    generation: -1,
    identity: "",
  });
  resumeRefresh.current.refresh = refresh;
  useEffect(() => {
    if (!enabled) return;
    const resume = () => {
      const current = material.current;
      if (!current) return;
      const generation = sessionGeneration.current;
      const identity = onboardingIdentity(current.keys, current.keyContext);
      let state = resumeRefresh.current;
      if (state.generation !== generation || state.identity !== identity) {
        state = {
          refresh: state.refresh,
          busy: false,
          lastStarted: 0,
          generation,
          identity,
        };
        resumeRefresh.current = state;
      }
      if (
        document.visibilityState === "hidden" ||
        !material.current ||
        !privateSessionIntent.current ||
        !idle.current.check() ||
        busy.current ||
        state.busy ||
        Date.now() - state.lastStarted < 3_000
      )
        return;
      state.busy = true;
      state.lastStarted = Date.now();
      // Read-only recovery: a wake-up never resubmits a payment.
      void state
        .refresh()
        .catch(() => undefined)
        .finally(() => {
          if (resumeRefresh.current === state) state.busy = false;
        });
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
    };
  }, [enabled]);

  const receiptVisible = useCallback((identity: string) => {
    const current = material.current;
    return current
      ? onboardingIdentity(current.keys, current.keyContext) === identity
      : privateSessionIntent.current &&
          expiredOperation.current?.identity === identity;
  }, []);

  const checkOperation = useCallback(async () => {
    const current = material.current;
    const identity = current
      ? onboardingIdentity(current.keys, current.keyContext)
      : expiredOperation.current?.identity;
    if (!identity) return;
    const pending = pendingRelays.current.get(identity);
    if (!pending || pending.checking || pending.operation.phase !== "pending")
      return;
    const show = (update: Partial<V2PrivateOperation>) => {
      pending.operation = { ...pending.operation, ...update };
      // Keep the shown progress (title, steps, bar) of the waiting action.
      if (receiptVisible(identity))
        setOperation((shown) =>
          shown?.id === pending.operation.id
            ? { ...shown, ...pending.operation }
            : shown
        );
    };
    pending.checking = true;
    show({ checking: true });
    try {
      const outcome = await services().chain.reconcileRelay(
        pending.prepared,
        pending.metadata,
        pending.operation.txHash
      );
      if (outcome.status === "unknown") {
        show({
          txHash: outcome.txHash ?? pending.operation.txHash,
          message:
            "Confirmation is still pending. Check status before submitting again.",
        });
        return;
      }
      pendingRelays.current.delete(identity);
      show({
        phase: outcome.status,
        txHash: outcome.txHash,
        message:
          outcome.status === "confirmed"
            ? pending.finalTransaction
              ? "Transaction confirmed."
              : "Your transaction has not been submitted. Review it to continue."
            : "Transaction failed on-chain. Review before trying again.",
      });
      const active = material.current;
      if (
        outcome.status === "confirmed" &&
        !busy.current &&
        active &&
        onboardingIdentity(active.keys, active.keyContext) === identity
      ) {
        const generation = sessionGeneration.current;
        const keys = copyKeys(active.keys);
        let next: Material | undefined;
        try {
          next = await recover(keys, active.keyContext);
          // A GET may complete after another intentional transaction starts.
          // Its earlier account snapshot must not overwrite that new action.
          if (
            generation === sessionGeneration.current &&
            material.current === active &&
            operationSequence.current === pending.operation.id &&
            !busy.current
          ) {
            publish(next, redacted.isPrivateMode);
          }
        } catch {
          show({
            message: pending.finalTransaction
              ? "Transaction confirmed. Balances are still updating."
              : "Your transaction has not been submitted. Refresh your balance and confirm again.",
          });
        } finally {
          if (!next || material.current !== next) wipeKeys(keys);
        }
      }
    } catch {
      show({
        message:
          "Status is temporarily unavailable. Check again; no new transaction was submitted.",
      });
    } finally {
      pending.checking = false;
      show({ checking: false });
    }
  }, [publish, recover, redacted.isPrivateMode, services]);

  const checkOperationRef = useRef(checkOperation);
  checkOperationRef.current = checkOperation;
  useEffect(() => {
    if (!enabled || operation?.phase !== "pending") return;
    // Receipt lookups only: an uncertain submission must never be resent.
    // Keep this at session scope so closing a modal cannot stop reconciliation.
    const check = () => {
      if (document.visibilityState !== "hidden")
        void checkOperationRef.current();
    };
    const first = window.setTimeout(check, 0);
    const timer = window.setInterval(check, 5_000);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [enabled, operation?.id, operation?.phase]);

  const expiry = useCallback(
    async (config: V2ContractConfig): Promise<bigint> => {
      if (config.max_expiry_delta < 1)
        throw new Error("Invalid Private Mode expiry policy");
      const { chain } = services();
      return (
        (await chain.height()) + BigInt(Math.min(config.max_expiry_delta, 300))
      );
    },
    [services]
  );

  const onboard = useCallback(
    async (
      starting: Material,
      expectedGeneration = sessionGeneration.current,
      busyAlreadyHeld = false,
      completedApprovals = 0,
      // "rotation" (Model B) creates one more sub for the next interaction.
      // It never changes the visible session status and returns instead of
      // publishing, so the caller keeps control of selection and receipts.
      mode: "account" | "rotation" = "account",
      onRotationPhase?: (phase: V2PrivacyProgress["phase"]) => void,
      // A new Spending account derives its salt from the keys, from the scan
      // right before its creation (spending.ts). Other subs use random bytes.
      saltFor?: (current: Material) => Uint8Array
    ): Promise<Material | undefined> => {
      const rotation = mode === "rotation";
      ensureRecoveryExportComplete(
        recoveryExportGate.current,
        "creating a private account"
      );
      if (busy.current && !busyAlreadyHeld)
        throw new Error("A private operation is already running");
      if (!selected) throw new Error("Connect a THORChain wallet");
      const operationKeys = copyKeys(starting.keys);
      const identity = onboardingIdentity(operationKeys, starting.keyContext);
      pendingOnboarding.current = pendingByIdentity.current.get(identity);
      const assertCurrentSession = (): void => {
        if (sessionGeneration.current !== expectedGeneration) {
          throw new Error("Private Mode changed while onboarding");
        }
        if (
          pendingOnboarding.current &&
          pendingOnboarding.current.identity !== identity
        ) {
          throw new Error("Private Account changed while onboarding");
        }
      };
      const approvalsCompleted = completedApprovals;
      const approvalsTotal = completedApprovals;
      const report = (phase: V2PrivacyProgress["phase"]): void => {
        if (rotation) {
          onRotationPhase?.(phase);
          return;
        }
        reportProgress(expectedGeneration, {
          phase,
          approvalsCompleted,
          approvalsTotal,
        });
      };
      // Rotation needs a sub that did not exist when it started.
      const baseline = new Set(
        activeControlNotes(starting)
          .map((note) => (note.note as ControlNote).subwallet)
          .filter((value): value is string => !!value)
      );
      const needsSubwallet = (candidate: Material): boolean =>
        !activeControlNotes(candidate).some((note) => {
          const subwallet = (note.note as ControlNote).subwallet;
          return !!subwallet && (!rotation || !baseline.has(subwallet));
        });
      const needsRegistration = (candidate: Material): boolean =>
        rotation
          ? !activeControlNotes(candidate).some(
              (note) => (note.note as ControlNote).subwallet === null
            )
          : activeControlNotes(candidate).length === 0;
      const expectedOutput = (
        prepared: PreparedV2Execution,
        subwallet: string | null
      ): string => {
        const note = prepared.outputs.find(
          (output): output is ControlNote =>
            output.kind === "control" && output.subwallet === subwallet
        );
        if (!note || !/^[0-9a-f]{64}$/.test(note.commitment)) {
          throw new Error(
            "Account preparation did not include its recovery commitment"
          );
        }
        return note.commitment;
      };
      const observe = async (pending: PendingOnboarding): Promise<Material> => {
        report(
          pending.kind === "registration"
            ? "registration-confirming"
            : "subwallet-confirming"
        );
        const current = await waitForOnboardingNote({
          pending,
          recover: () => recover(operationKeys, starting.keyContext),
          assertCurrent: assertCurrentSession,
        });
        assertCurrentSession();
        // Keep confirmed registration pinned until creation replaces it. A
        // later proving/relayer failure must not permit a lagging empty scan
        // to trigger another paid registration on retry.
        if (
          pending.kind === "subwallet" &&
          pendingOnboarding.current === pending
        ) {
          pendingByIdentity.current.delete(pending.identity);
          pendingOnboarding.current = undefined;
        }
        return current;
      };
      // A returned rotation result still owns operationKeys.
      let rotated: Material | undefined;
      if (!busyAlreadyHeld) busy.current = true;
      if (!rotation) {
        setStatus("onboarding");
        setError(undefined);
      }
      report("verifying");
      try {
        assertCurrentSession();
        const { chain } = services();
        await chain.attestProxyDeployment();
        assertCurrentSession();
        const contractConfig = await chain.contractConfig();
        assertCurrentSession();
        const registrationSponsor = async (): Promise<V2RelayerMetadata> => {
          const sponsor = await chain.relayerMetadata().catch(() => {
            throw new Error(REGISTRATION_SPONSOR_UNAVAILABLE);
          });
          assertCurrentSession();
          if (
            !sponsor.sponsoredActions.has("register_control") ||
            !sponsor.sponsoredActions.has("create_subwallet") ||
            sponsor.registrationSponsorAddress !== sponsor.address ||
            sponsor.registrationFee?.denom !==
              contractConfig.registration_fee.denom ||
            sponsor.registrationFee?.amount !==
              contractConfig.registration_fee.amount
          )
            throw new Error(REGISTRATION_SPONSOR_UNAVAILABLE);
          return sponsor;
        };
        const relayRegistration = async (
          submitted: PendingOnboarding
        ): Promise<void> => {
          const registrationRequest = submitted.registrationRequest;
          if (!registrationRequest)
            throw new Error("Registration recovery request is unavailable");
          const sponsor = await registrationSponsor();
          assertCurrentSession();
          pendingOnboarding.current = submitted;
          pendingByIdentity.current.set(identity, submitted);
          report("registration-relaying");
          try {
            // A busy or briefly paused relayer is waited out with the same
            // public request; nothing was submitted in that case.
            submitted.txHash = await relayResubmittingOnPreflight(() =>
              chain.relay(
                {
                  msg: registrationRequest,
                  funds: [contractConfig.registration_fee],
                  outputs: [],
                  consumedNullifiers: [],
                },
                sponsor
              )
            );
          } catch (cause) {
            if (isV2RelayOutcomeUnknownError(cause))
              submitted.txHash = cause.transactionHash;
            // Keep the same public request even when a sponsor is empty. A
            // later manual retry cannot generate an unrelated registration.
            throw cause;
          } finally {
            pendingByIdentity.current.set(identity, submitted);
          }
          assertCurrentSession();
        };
        let proofGenerator: ProofGenerator | undefined;
        const proving = async () => {
          proofGenerator ??= await prove();
          assertCurrentSession();
          return proofGenerator;
        };
        // Signing in to an account that already has its Spending account only
        // reads the chain. An unfinished "New Spending account" (a saved
        // registration, e.g. refused while THORChain halted contracts) waits
        // for the next one, which resumes it, instead of blocking sign-in.
        const pending =
          rotation || needsSubwallet(starting)
            ? pendingOnboarding.current
            : undefined;
        const deferred = !!pendingOnboarding.current && !pending;
        if (deferred) pendingOnboarding.current = undefined;
        let registrationObserved = pending?.kind === "registration";
        report("recovering");
        let current: Material;
        if (pending?.kind === "registration" && pending.registrationRequest) {
          current = await recover(operationKeys, starting.keyContext);
          assertCurrentSession();
          if (!hasPendingOnboardingNote(current, pending)) {
            // The backend journals the public commitment before broadcasting;
            // exact replay reconciles or resumes it, including after reload.
            // Reconcile even a known hash: a once-unknown transaction can
            // later fail on-chain. The backend may retry only after verifying
            // that failure; success/unknown keep the original submission.
            await relayRegistration(pending);
            current = await observe(pending);
          }
        } else if (
          pending?.kind === "subwallet" &&
          pending.creationRequest &&
          pending.creationRelayer
        ) {
          current = await recover(operationKeys, starting.keyContext);
          assertCurrentSession();
          if (!hasPendingOnboardingNote(current, pending)) {
            const result = await chain.reconcileRelay(
              {
                msg: pending.creationRequest,
                funds: [],
                outputs: [],
                consumedNullifiers: [],
              },
              { address: pending.creationRelayer },
              pending.txHash
            );
            assertCurrentSession();
            if (result.status === "failed") {
              // Only an exact mined failure proves creation did not consume
              // the registered control note. A missing receipt never does.
              const registered: PendingOnboarding = {
                identity,
                kind: "registration",
                commitment: pending.registrationCommitment!,
              };
              pendingOnboarding.current = registered;
              pendingByIdentity.current.set(identity, registered);
              registrationObserved = true;
            } else {
              if (result.txHash) pending.txHash = result.txHash;
              pendingByIdentity.current.set(identity, pending);
              current = await observe(pending);
            }
          }
        } else {
          current = pending
            ? await observe(pending)
            : await recover(operationKeys, starting.keyContext);
        }
        assertCurrentSession();
        if (needsSubwallet(current)) {
          if (needsRegistration(current)) {
            if (registrationObserved) {
              throw new Error(
                "Registration was confirmed, but its active account note is unavailable. Refresh recovery before continuing."
              );
            }
            // Check sponsorship before generating a proof. Never fall back to
            // registration paid by the connected wallet.
            await registrationSponsor();
            report("registration-proof");
            const registration = await prepareControlRegistration(
              {
                keys: current.keys,
                context: current.keyContext,
                prove: await proving(),
              },
              contractConfig.registration_fee
            );
            assertCurrentSession();
            const submitted: PendingOnboarding = {
              identity,
              kind: "registration",
              commitment: expectedOutput(registration, null),
              registrationRequest: registration.msg as NonNullable<
                PendingOnboarding["registrationRequest"]
              >,
            };
            // Pin the commitment before the request can reach the relayer.
            // An unknown response must only trigger recovery, never a new note.
            await relayRegistration(submitted);
            assertCurrentSession();
            current = await observe(submitted);
            registrationObserved = true;
          }
          if (needsSubwallet(current)) {
            const input = activeControlNotes(current).find(
              (note) => (note.note as ControlNote).subwallet === null
            );
            if (!input)
              throw new Error(
                "The confirmed account note is not yet available for account creation. Please refresh recovery."
              );
            report("subwallet-proof");
            const relayer = await chain.relayerMetadata();
            assertCurrentSession();
            if (!relayer.sponsoredActions.has("create_subwallet")) {
              throw new Error(REGISTRATION_SPONSOR_UNAVAILABLE);
            }
            const salt = saltFor
              ? saltFor(current)
              : crypto.getRandomValues(new Uint8Array(32));
            const predictedSubwallet = chain.predictSubwallet(
              salt,
              contractConfig
            );
            const creation = await prepareCreateSubwallet(
              {
                keys: current.keys,
                context: current.keyContext,
                prove: await proving(),
                relayerAddress: relayer.address,
                expiryHeight: await expiry(contractConfig),
              },
              { root: current.recovery.root, input, salt, predictedSubwallet }
            );
            assertCurrentSession();
            const submitted: PendingOnboarding = {
              identity,
              kind: "subwallet",
              commitment: expectedOutput(creation, predictedSubwallet),
              subwallet: predictedSubwallet,
              creationRequest: creation.msg as NonNullable<
                PendingOnboarding["creationRequest"]
              >,
              creationRelayer: relayer.address,
              registrationCommitment: input.note.commitment,
            };
            const confirmedRegistration =
              pendingOnboarding.current?.kind === "registration"
                ? pendingOnboarding.current
                : {
                    identity,
                    kind: "registration" as const,
                    commitment: input.note.commitment,
                  };
            pendingOnboarding.current = submitted;
            pendingByIdentity.current.set(identity, submitted);
            report("subwallet-relaying");
            await relayResubmittingOnPreflight(() =>
              chain.relay(creation, relayer)
            )
              .then((hash) => {
                submitted.txHash = hash;
              })
              .catch((cause: unknown) => {
                if (
                  isV2RelayRejectedError(cause) &&
                  pendingOnboarding.current === submitted
                ) {
                  pendingOnboarding.current = confirmedRegistration;
                  if (confirmedRegistration)
                    pendingByIdentity.current.set(
                      identity,
                      confirmedRegistration
                    );
                  else pendingByIdentity.current.delete(identity);
                }
                if (isV2RelayOutcomeUnknownError(cause))
                  submitted.txHash = cause.transactionHash;
                throw cause;
              })
              .finally(() => {
                if (pendingOnboarding.current === submitted) {
                  pendingByIdentity.current.set(identity, submitted);
                }
              });
            assertCurrentSession();
            current = await observe(submitted);
          }
        }
        if (needsSubwallet(current)) {
          throw new Error(
            "Private Account creation was not confirmed by the chain"
          );
        }
        assertCurrentSession();
        pendingOnboarding.current = undefined;
        // A deferred record stays saved for the next new Spending account.
        if (!deferred) pendingByIdentity.current.delete(identity);
        if (rotation) {
          rotated = current;
          return current;
        }
        publish(current, true);
        return undefined;
      } catch (cause) {
        reportProgress(expectedGeneration);
        const message = privateErrorMessage(cause);
        if (rotation) throw new Error(message);
        if (sessionGeneration.current === expectedGeneration) {
          setError(message);
          const current = material.current;
          setStatus(
            !current
              ? "error"
              : activeControlNotes(current).some(
                    (note) => (note.note as ControlNote).subwallet
                  )
                ? "ready"
                : "needs-onboarding"
          );
        }
        throw new Error(message);
      } finally {
        reportProgress(expectedGeneration);
        if (material.current?.keys !== operationKeys && !rotated)
          wipeKeys(operationKeys);
        if (!busyAlreadyHeld) busy.current = false;
      }
    },
    [expiry, prove, publish, recover, reportProgress, selected, services]
  );

  const keyContext = useCallback((): V2KeyContext => {
    if (!walletAddress) throw new Error("Connect a THORChain wallet");
    const { deployment } = services();
    return {
      chainId: deployment.chainId,
      proxyAddress: deployment.proxyContract,
      walletAddress,
    };
  }, [services, walletAddress]);

  const continueWalletUnlock = useCallback(() => {
    const pending = walletSignatureContinuation.current;
    if (!pending || pending.generation !== sessionGeneration.current) return;
    walletSignatureContinuation.current = undefined;
    pending.resolve();
  }, []);

  const cancelWalletUnlock = useCallback(() => {
    const pending = walletSignatureContinuation.current;
    if (!pending || pending.generation !== sessionGeneration.current) return;
    walletSignatureContinuation.current = undefined;
    pending.reject(new Error("Wallet unlock cancelled"));
  }, []);

  const activateWithWallet = useCallback(
    async (expectedAccountId?: string) => {
      if (!selected) throw new Error("Connect a THORChain wallet");
      if (busy.current)
        throw new Error("A private operation is already running");
      ensureRecoveryExportComplete(
        recoveryExportGate.current,
        "switching accounts"
      );
      const generation = ++sessionGeneration.current;
      let derivedKeys: V2Keys | undefined;
      let approvalsCompleted = 0;
      const assertCurrentSession = (): void => {
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while unlocking");
        }
      };
      const reportSignature = (current: number): void => {
        reportProgress(generation, {
          phase: "wallet-signature",
          approvalsCompleted,
          // The key derivation requires two equal message signatures. Keep
          // their known substeps separate from any later registration approval.
          messageSignature: { current, total: 2 },
        });
      };
      busy.current = true;
      setStatus("unlocking");
      setError(undefined);
      reportSignature(1);
      try {
        const context = keyContext();
        const signer = accounts.signer(selected.address.address);
        if (!signer.signMessage)
          throw new Error("The connected wallet cannot sign messages");
        // Wallet adapters can request a fresh user action between approvals.
        // Keep Keplr's existing mobile behavior and all derivation bytes intact.
        const requiresWalletHandoff =
          signer.requiresMessageSigningHandoff === true ||
          (selected.provider === "Keplr" &&
            window.keplr?.mode === "mobile-web");
        derivedKeys = await deriveKeysFromWallet({
          context,
          signDerivationMessage: async (message) => {
            assertCurrentSession();
            if (requiresWalletHandoff && approvalsCompleted === 1) {
              await new Promise<void>((resolve, reject) => {
                walletSignatureContinuation.current = {
                  generation,
                  resolve,
                  reject,
                };
                reportProgress(generation, {
                  phase: "wallet-signature-ready",
                  approvalsCompleted,
                  messageSignature: { current: 2, total: 2 },
                });
              });
              assertCurrentSession();
            }
            reportSignature(approvalsCompleted + 1);
            const signature = await signer.signMessage!(
              context.chainId,
              context.walletAddress,
              message
            );
            assertCurrentSession();
            if (!signature) throw new Error("Wallet signature was cancelled");
            approvalsCompleted += 1;
            reportSignature(approvalsCompleted);
            return signature.signature;
          },
        });
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while unlocking");
        }
        reportProgress(generation, { phase: "recovering", approvalsCompleted });
        if (expectedAccountId && derivedKeys.recoveryId !== expectedAccountId)
          throw new Error(
            "This wallet did not unlock the selected account. Use its recovery backup instead."
          );
        const next = await recover(derivedKeys, context);
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while unlocking");
        }
        accountCatalog().remember(derivedKeys, context, {
          walletDerived: true,
        });
        pendingOnboarding.current = pendingByIdentity.current.get(
          onboardingIdentity(derivedKeys, context)
        );
        await onboard(next, generation, true, approvalsCompleted);
      } catch (cause) {
        reportProgress(generation);
        const message = cause instanceof Error ? cause.message : String(cause);
        if (generation !== sessionGeneration.current) throw cause;
        if (material.current) {
          try {
            const latest = await recover(
              material.current.keys,
              material.current.keyContext
            );
            assertCurrentSession();
            publish(
              latest,
              activeControlNotes(latest).some(
                (note) => (note.note as ControlNote).subwallet
              )
            );
          } catch {
            if (generation !== sessionGeneration.current) throw cause;
            setStatus("error");
          }
        }
        setError(message);
        if (!material.current) setStatus("error");
        throw cause;
      } finally {
        if (walletSignatureContinuation.current?.generation === generation)
          walletSignatureContinuation.current = undefined;
        reportProgress(generation);
        // `onboard` makes and publishes its own defensive key copy. The raw
        // wallet-derived set is only a transition value and must never outlive
        // this unlock attempt, including on recovery/proof/relayer failures.
        if (derivedKeys) wipeKeys(derivedKeys);
        busy.current = false;
      }
    },
    [
      accounts,
      accountCatalog,
      keyContext,
      onboard,
      publish,
      recover,
      reportProgress,
      selected,
      setRecoveryExportGate,
    ]
  );

  const createRandomRecovery = useCallback(
    async (passphrase: string): Promise<string> => {
      if (busy.current)
        throw new Error("A private operation is already running");
      ensureRecoveryExportComplete(
        recoveryExportGate.current,
        "creating another account"
      );
      const generation = ++sessionGeneration.current;
      const context = keyContext();
      let keys: V2Keys | undefined;
      busy.current = true;
      try {
        keys = await generateRandomKeys(context);
        const serialized = JSON.stringify(
          await createRecoveryPackage(keys, context, passphrase),
          null,
          2
        );
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while creating recovery");
        }
        const next = await recover(keys, context);
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while creating recovery");
        }
        accountCatalog().remember(keys, context);
        pendingRecoveryBackups.current.set(keys.recoveryId, serialized);
        setRecoveryExportGate(true);
        publish(next, false);
        return serialized;
      } finally {
        if (keys && material.current?.keys !== keys) wipeKeys(keys);
        busy.current = false;
      }
    },
    [accountCatalog, keyContext, publish, recover, setRecoveryExportGate]
  );

  const confirmRecoveryExport = useCallback(() => {
    const current = material.current;
    if (!current) throw new Error("Create a recovery key first");
    const backup = pendingRecoveryBackups.current.get(current.keys.recoveryId);
    if (backup) {
      accountCatalog().remember(current.keys, current.keyContext, { backup });
      pendingRecoveryBackups.current.delete(current.keys.recoveryId);
      setRevision((value) => value + 1);
    }
    setRecoveryExportGate(false);
  }, [accountCatalog, setRecoveryExportGate]);

  const importRecovery = useCallback(
    async (
      serialized: string,
      passphrase: string,
      expectedAccountId?: string
    ): Promise<void> => {
      if (busy.current)
        throw new Error("A private operation is already running");
      const generation = ++sessionGeneration.current;
      let keys: V2Keys | undefined;
      busy.current = true;
      setStatus("unlocking");
      setError(undefined);
      try {
        const connectedContext = keyContext();
        const value = strictRecoveryPackage(serialized);
        const context = contextForRecoveryPackage(value, connectedContext);
        keys = await openRecoveryPackage(value, context, passphrase);
        if (expectedAccountId && keys.recoveryId !== expectedAccountId)
          throw new Error(
            "This backup does not belong to the selected account"
          );
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while importing recovery");
        }
        await services().chain.attestProxyDeployment();
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while importing recovery");
        }
        const next = await recover(keys, context);
        if (generation !== sessionGeneration.current) {
          throw new Error("Private Mode changed while importing recovery");
        }
        accountCatalog().remember(keys, context, { backup: serialized });
        setRecoveryExportGate(false);
        if (
          activeControlNotes(next).some(
            (note) => (note.note as ControlNote).subwallet
          )
        ) {
          publish(next, true);
        } else {
          publish(next, false);
        }
      } catch (cause) {
        if (generation === sessionGeneration.current) {
          setError(
            privateErrorMessage(
              cause,
              "Could not unlock this recovery backup. Check the file and passphrase, then try again."
            )
          );
          // Import failure must leave the current identity and backup gate
          // untouched, but must not strand the UI in the busy unlocking state.
          const current = material.current;
          setStatus(
            !current
              ? "error"
              : activeControlNotes(current).some(
                    (note) => (note.note as ControlNote).subwallet
                  )
                ? "ready"
                : "needs-onboarding"
          );
        }
        throw new Error(
          privateErrorMessage(
            cause,
            "Could not unlock this recovery backup. Check the file and passphrase, then try again."
          )
        );
      } finally {
        if (keys && material.current?.keys !== keys) wipeKeys(keys);
        busy.current = false;
      }
    },
    [
      accountCatalog,
      keyContext,
      publish,
      recover,
      services,
      setRecoveryExportGate,
    ]
  );

  const exportRecovery = useCallback(
    async (passphrase: string): Promise<string> => {
      assertSessionAccess();
      const current = material.current;
      if (!current) throw new Error("Unlock Private Mode first");
      if (busy.current)
        throw new Error("A private operation is already running");
      const generation = sessionGeneration.current;
      const exportKeys = copyKeys(current.keys);
      busy.current = true;
      try {
        const serialized = JSON.stringify(
          await createRecoveryPackage(
            exportKeys,
            current.keyContext,
            passphrase
          ),
          null,
          2
        );
        assertSessionAccess();
        if (
          generation !== sessionGeneration.current ||
          material.current !== current
        )
          throw new Error(
            "Private Mode changed while backing up. Please try again."
          );
        if (recoveryExportGate.current)
          pendingRecoveryBackups.current.set(
            current.keys.recoveryId,
            serialized
          );
        else
          accountCatalog().remember(current.keys, current.keyContext, {
            backup: serialized,
          });
        setRevision((value) => value + 1);
        return serialized;
      } finally {
        wipeKeys(exportKeys);
        busy.current = false;
      }
    },
    [accountCatalog, assertSessionAccess]
  );

  const selectAccount = useCallback(
    async (id: string, passphrase?: string) => {
      if (busy.current)
        throw new Error("A private operation is already running");
      if (material.current?.keys.recoveryId === id) return;
      ensureRecoveryExportComplete(
        recoveryExportGate.current,
        "switching accounts"
      );
      const saved = accountCatalog().get(id);
      const cached = accountCatalog().unlocked(id);
      if (!cached) {
        if (passphrase !== undefined && saved.backup) {
          await importRecovery(saved.backup, passphrase, id);
          return;
        }
        if (
          saved.walletDerived &&
          saved.walletAddress.toLowerCase() === walletAddress?.toLowerCase()
        ) {
          await activateWithWallet(id);
          return;
        }
        throw new Error(
          saved.backup
            ? "Enter this account’s backup passphrase to unlock it"
            : "Connect the original wallet to unlock this account"
        );
      }
      const generation = ++sessionGeneration.current;
      busy.current = true;
      setStatus("unlocking");
      setError(undefined);
      try {
        await services().chain.attestProxyDeployment();
        if (generation !== sessionGeneration.current)
          throw new Error("Private Mode changed while switching accounts");
        const next = await recover(cached.keys, cached.context);
        if (generation !== sessionGeneration.current)
          throw new Error("Private Mode changed while switching accounts");
        publish(next, true);
      } catch (cause) {
        if (generation === sessionGeneration.current) {
          setError(
            privateErrorMessage(
              cause,
              "Could not open this account. Try again."
            )
          );
          setStatus(
            material.current
              ? selectedSubwallet
                ? "ready"
                : "needs-onboarding"
              : "locked"
          );
        }
        throw cause;
      } finally {
        if (material.current?.keys !== cached.keys) wipeKeys(cached.keys);
        busy.current = false;
      }
    },
    [
      accountCatalog,
      activateWithWallet,
      importRecovery,
      publish,
      recover,
      selectedSubwallet,
      services,
      walletAddress,
    ]
  );

  const renameAccount = useCallback(
    (id: string, label: string) => {
      accountCatalog().rename(id, label);
      setRevision((value) => value + 1);
    },
    [accountCatalog]
  );

  const removeAccount = useCallback(
    (id: string) => {
      if (busy.current)
        throw new Error("A private operation is already running");
      if (material.current?.keys.recoveryId === id)
        throw new Error(
          "Switch accounts before removing this account from this browser"
        );
      accountCatalog().remove(id);
      setRevision((value) => value + 1);
    },
    [accountCatalog]
  );

  const onboardCurrent = useCallback(async () => {
    const current = material.current;
    if (!current) throw new Error("Create or import a recovery key first");
    ensureRecoveryExportComplete(
      recoveryExportGate.current,
      "opening a private account"
    );
    if (busy.current) throw new Error("A private operation is already running");
    if (
      !pendingOnboarding.current &&
      activeControlNotes(current).some(
        (note) => (note.note as ControlNote).subwallet
      )
    ) {
      const generation = sessionGeneration.current;
      await services().chain.attestProxyDeployment();
      if (
        generation !== sessionGeneration.current ||
        material.current !== current
      ) {
        throw new Error("Private Mode changed while opening the account");
      }
      publish(current, true);
      return;
    }
    await onboard(current);
  }, [onboard, publish, services]);

  const selectSubwallet = useCallback(
    (address: string) => {
      const current = material.current;
      if (
        !current ||
        !activeControlNotes(current).some(
          (note) => (note.note as ControlNote).subwallet === address
        )
      ) {
        throw new Error("Unknown recovered Private Account");
      }
      setSelectedSubwallet(address);
      if (address !== currentFeeSelection.current.subwallet)
        setOperation(undefined);
      redacted.setSubWalletAddress(address);
    },
    [redacted]
  );

  const actionContext = useCallback(
    async (
      metadata: V2RelayerMetadata,
      proofGenerator: ProofGenerator,
      current: Material
    ) => {
      const { chain } = services();
      const config = await chain.contractConfig();
      return {
        keys: current.keys,
        context: current.keyContext,
        prove: proofGenerator,
        relayerAddress: metadata.address,
        expiryHeight: await expiry(config),
      };
    },
    [expiry, services]
  );

  const feeBinding = useCallback(
    (message: Msg): string => {
      assertSessionAccess();
      const keys = material.current?.keys;
      if (
        !keys ||
        !currentFeeSelection.current.subwallet ||
        !currentFeeSelection.current.wallet
      )
        throw new Error("Unlock Private Mode first");
      return JSON.stringify({
        generation: sessionGeneration.current,
        identity: feeIdentityEpoch.current,
        ...currentFeeSelection.current,
        // Model B picks (or creates) the one-time sub at execution. A review
        // binds to "new interaction" or to the pinned position's sub instead.
        // Model B binds the exact one-time (or pinned position) sub: product
        // messages embed it, e.g. as the swap's destination address.
        ...(modelBEnabled && {
          subwallet: `model-b:${
            interactionSubwallet(
              material.current,
              pinnedRef.current,
              positionsRef.current
            ) ?? "none"
          }`,
        }),
        account: message.account,
        chainId: services().deployment.chainId,
        proxy: services().deployment.proxyContract,
      });
    },
    [assertSessionAccess, modelBEnabled, services]
  );

  // Opening an account never needs proving files; the first fee quote (the
  // user is about to act) warms them, so confirming downloads nothing.
  const provingWarm = useRef(false);
  const warmProofs = useCallback(() => {
    if (provingWarm.current) return;
    provingWarm.current = true;
    void Promise.resolve()
      .then(() => loadProvingManifest(services().deployment))
      .then((manifest) =>
        prefetchProvingArtifacts(manifest, [
          "reserve_spend",
          "reserve_merge",
          "control_action",
          // Returning funds to the Reserve (claw-back) proves a deposit note.
          "reserve_deposit",
        ])
      )
      .catch(() => {
        provingWarm.current = false;
      });
  }, [services]);

  /** Keeps the block time estimate fresh (every 10 min), off the critical path. */
  const refreshBlockTime = useCallback(() => {
    if (Date.now() - blockTimeReadAt.current <= 10 * 60_000) return;
    blockTimeReadAt.current = Date.now();
    void Promise.resolve()
      .then(() => services().chain.blockTimeMs())
      .then((measured) => {
        if (measured) blockTimeMs.current = measured;
      })
      .catch(() => undefined);
  }, [services]);

  /**
   * Claw-back timing. Single mode checks the Spending sub one block after an
   * action and then every block for a while, so a payout (swap output, unstake,
   * refund) returns to the Reserve as soon as it lands. Rotate mode keeps a
   * randomized delay, so a return never lands next to its one-time action.
   */
  const scheduleSweep = useCallback(
    (afterAction = true) => {
      if (!modelBEnabled) return;
      const override = sweepDelayOverride();
      let at: number;
      if (privateWalletMode() === "single") {
        if (afterAction) watchUntil.current = Date.now() + SWEEP_WATCH_MS;
        // A FIN swap's output is already in the sub when the action is
        // confirmed: check right away, then every block while watching.
        at = Date.now() + (override ?? (afterAction ? 1_500 : 3_000));
      } else at = Date.now() + sweepDelayMs(Math.random, override);
      nextSweepAtRef.current = at;
      setNextSweepAt(at);
    },
    [modelBEnabled]
  );

  /** Read-only: settled balances per owned sub (shown as "returning"). */
  const readSubBalances = useCallback(
    async (addresses?: readonly string[]) => {
      const read = new Map<string, V2Coin[]>();
      const current = material.current;
      if (!modelBEnabled || !current) return read;
      const generation = sessionGeneration.current;
      const { chain } = services();
      const targets =
        addresses ?? subwalletInfos(current).map((info) => info.address);
      const balances = await Promise.all(
        targets.map((address) => chain.bankBalances(address))
      );
      targets.forEach((address, index) => read.set(address, balances[index]));
      if (generation !== sessionGeneration.current) return read;
      setSubBalances((previous) => {
        const next = new Map(addresses ? previous : []);
        read.forEach((coins, address) => {
          if (coins.some((coin) => BigInt(coin.amount) > 0n))
            next.set(address, coins);
          else next.delete(address);
        });
        // Block-by-block checks must not re-render the app when nothing moved.
        return sameSubBalances(previous, next) ? previous : next;
      });
      return read;
    },
    [modelBEnabled, services]
  );

  /**
   * Public pool data for one scanned material: the Reserve totals read after
   * the scan, and a tree read showing whether the pool moved since. The same
   * requests for every user, whatever they hold.
   */
  const readPrivacyPool = useCallback(
    async (current: Material): Promise<PrivacyPoolData> => {
      const { chain } = services();
      const after = await chain.reserveTotals({
        notBefore: current.scan?.finishedAt ?? Date.now(),
      });
      if (
        !poolTotals.current ||
        poolTotals.current.startedAt <= after.startedAt
      )
        poolTotals.current = after;
      let unchangedSinceScan = false;
      let ownUnchangedSinceScan = false;
      try {
        const tree = await retryTransientRead(() =>
          chain.recoverySource.snapshot()
        );
        const pool = current.recovery.pool;
        unchangedSinceScan =
          !!pool &&
          tree.nextIndex === pool.nextIndex &&
          tree.nextNullifierIndex === pool.nextNullifierIndex &&
          tree.root === current.recovery.root;
        // Others moving the pool does not make the totals wrong for this
        // scan: only the user's own notes must still be the scanned ones.
        // The check reads just the records and nullifiers added since.
        if (pool && !unchangedSinceScan)
          ownUnchangedSinceScan = !(await retryTransientRead(() =>
            ownReserveChangedSince({
              keys: current.keys,
              context: current.keyContext,
              source: chain.recoverySource,
              scanned: pool,
              now: tree,
              unspentNullifiers: new Set(
                current.recovery.activeNotes
                  .filter((entry) => entry.note.kind === "reserve")
                  .map((entry) => entry.nullifier)
              ),
            })
          ));
      } catch {
        // Then only a total that did not move vouches for the scan.
      }
      return {
        before: current.scan?.totalsBefore,
        after,
        unchangedSinceScan,
        ownUnchangedSinceScan,
        checkedAt: Date.now(),
      };
    },
    [services]
  );

  /**
   * Local Reserve privacy assessment of the published material. Previous
   * labels stay visible while new pool data is read (`checking`). Any failure
   * makes the affected assets unknown; nothing is logged.
   */
  const assessPrivacy = useCallback(async () => {
    const current = material.current;
    if (!modelBEnabled || !current) return;
    const run = ++privacyRun.current;
    // Lock, wipe and account switches raise `privacyRun` and replace the
    // material. The session generation is not compared: a failed import or
    // unlock raises it without publishing anything, and this run would then
    // give up with "checking" on and nothing left to run it again.
    const stale = () =>
      run !== privacyRun.current || material.current !== current;
    try {
      let pool = privacyPool.current;
      const reading = pool?.material !== current;
      if (!pool || reading) {
        const data = readPrivacyPool(current);
        // Awaited below; a failure must never become an unhandled rejection.
        data.catch(() => undefined);
        pool = { material: current, data };
        privacyPool.current = pool;
      }
      const identity = onboardingIdentity(current.keys, current.keyContext);
      if (privacyIdentity.current !== identity) {
        // Another account: never show its labels while this one is checked.
        privacyIdentity.current = identity;
        setPrivacy({ byDenom: new Map(), checking: true });
      } else if (reading)
        setPrivacy((previous) =>
          previous.checking ? previous : { ...previous, checking: true }
        );
      let data: PrivacyPoolData | undefined;
      try {
        data = await pool.data;
      } catch {
        data = undefined;
      }
      if (stale()) return;
      // A carried-over limit ends with its note.
      for (const entry of current.recovery.notes ?? [])
        if (entry.spent) privacyCarried.current.delete(entry.note.commitment);
      const input = privacyInput(
        current,
        subBalancesRef.current,
        data,
        dustPolicy.current,
        privacyCarried.current,
        pendingMovesRef.current
      );
      let byDenom: Map<string, V2PrivacyAssessment>;
      let limitedNotes: ReadonlySet<string>;
      try {
        ({ byDenom, limitedNotes } = assessReservePrivacyDetailed(input));
      } catch {
        byDenom = unavailableReservePrivacy(input);
        limitedNotes = new Set();
      }
      // A check that lacks pool data (a rate-limited read, say) keeps the last
      // good label of an asset for a while, as long as the user's own notes
      // of that asset are the same ones: only others could have moved, and
      // a re-read follows. The user's own moves never keep an old label.
      const ownNotes = ownNotesByDenom(input.notes);
      const previous = privacyShown.current;
      let keptAt: number | undefined;
      if (
        previous?.identity === identity &&
        previous.at !== undefined &&
        Date.now() - previous.at <= PRIVACY_KEEP_MS
      )
        for (const [denom, entry] of byDenom) {
          const before = previous.byDenom.get(denom);
          if (
            entry.reasons.includes("data_unavailable") &&
            before &&
            before.level !== "unknown" &&
            previous.notes?.get(denom) === ownNotes.get(denom)
          ) {
            byDenom.set(denom, before);
            keptAt = previous.at;
          }
        }
      privacyShown.current = {
        byDenom,
        limitedNotes,
        identity,
        at: keptAt ?? Date.now(),
        notes: ownNotes,
      };
      setPrivacy({
        byDenom,
        checking: false,
        // An asset still showing an earlier label: say when that was checked.
        updatedAt: keptAt ?? data?.checkedAt ?? Date.now(),
      });
      // Pool data missing for some asset (a rate-limited read, say): read it
      // again a few times for this scan, one pending re-read at a time. Only
      // an actual re-read counts; a new scan starts over.
      if (privacyRetry.current.material !== current) {
        clearTimeout(privacyRetry.current.timer);
        privacyRetry.current = { material: current, count: 0 };
      }
      const retry = privacyRetry.current;
      const unavailable = [...byDenom.values()].some((entry) =>
        entry.reasons.includes("data_unavailable")
      );
      if (
        unavailable &&
        retry.timer === undefined &&
        retry.count < PRIVACY_RETRY_DELAYS_MS.length
      )
        retry.timer = setTimeout(() => {
          retry.timer = undefined;
          if (material.current !== current || privacyRetry.current !== retry)
            return;
          retry.count += 1;
          if (privacyPool.current?.material === current)
            privacyPool.current = undefined;
          setPrivacyRetryTick((tick) => tick + 1);
        }, PRIVACY_RETRY_DELAYS_MS[retry.count]);
    } catch {
      // Never logged: the failure may concern the user's own notes.
      if (!stale()) {
        privacyShown.current = undefined;
        setPrivacy({
          byDenom: new Map(),
          checking: false,
          updatedAt: Date.now(),
        });
      }
    }
  }, [modelBEnabled, readPrivacyPool]);

  // Re-assess after every published scan and whenever the Spending balances
  // change, debounced. Only the Model B wallet shows the indicator.
  useEffect(() => {
    const current = material.current;
    if (!modelBEnabled || !current) return;
    // A new scan shows "checking" at once; the previous labels stay.
    if (privacyPool.current?.material !== current)
      setPrivacy((previous) =>
        previous.checking ? previous : { ...previous, checking: true }
      );
    const timer = setTimeout(() => {
      void assessPrivacy();
    }, PRIVACY_ASSESS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [assessPrivacy, modelBEnabled, revision, subBalances, privacyRetryTick]);

  // A pending re-read never outlives the session provider.
  useEffect(() => () => clearTimeout(privacyRetry.current.timer), []);

  /**
   * Before one of the user's own executions is sent: the Reserve notes it
   * creates from limited funds keep that limit (returning funds and moving
   * one's own notes never improve a label). `from` is the scan it was proven
   * against. Reads only the prepared wire messages and the labels last shown;
   * memory only, and never in the way of the execution itself.
   */
  const carryPrivacyLimits = useCallback(
    (prepared: PreparedV2Execution, from: Material) => {
      if (!modelBEnabled) return;
      try {
        const shown = privacyShown.current;
        const recovered = from.recovery.notes ?? from.recovery.activeNotes;
        const commitments = new Map(
          recovered.map((entry) => [entry.nullifier, entry.note.commitment])
        );
        const carried = carriedLimits(prepared.bundle ?? [prepared.msg], {
          note: (nullifier) => {
            const commitment = commitments.get(nullifier);
            return (
              commitment !== undefined &&
              (privacyCarried.current.has(commitment) ||
                !!shown?.limitedNotes.has(commitment))
            );
          },
          asset: (denom) => {
            const label = shown?.byDenom.get(denom);
            // Not assessed yet: a balance with nothing in the Reserve is
            // Limited all the same.
            return label
              ? label.level === "limited"
              : !recovered.some(
                  (entry) =>
                    !entry.spent &&
                    entry.note.kind === "reserve" &&
                    entry.note.denom === denom &&
                    privacyUnits(entry.note.amount) > 0n
                );
          },
        });
        for (const commitment of carried)
          privacyCarried.current.add(commitment);
      } catch {
        // The indicator never disturbs an operation.
      }
    },
    [modelBEnabled]
  );

  /**
   * One background job at a time, holding the operation lock while it runs.
   * The job calls `checkpoint` before it proves or sends anything; when an
   * action asked it to yield, the checkpoint stops it there.
   */
  const runBackground = useCallback(
    (
      kind: "preparing" | "returning" | "tidying",
      job: (generation: number, checkpoint: () => void) => Promise<void>
    ) => {
      if (
        !modelBEnabled ||
        busy.current ||
        backgroundJob.current ||
        statusRef.current !== "ready" ||
        !material.current
      )
        return;
      const generation = sessionGeneration.current;
      busy.current = true;
      setBackground(kind);
      const control = { requested: false };
      backgroundYield.current = control;
      const checkpoint = () => {
        if (control.requested) throw new V2BackgroundYield();
      };
      const promise = (async () => {
        try {
          await job(generation, checkpoint);
          if (generation === sessionGeneration.current)
            setModelBError(undefined);
        } catch (cause) {
          // Stopped for an action before anything was sent: it runs again
          // once the action is done (the action schedules the next check).
          if (cause instanceof V2BackgroundYield) return;
          if (generation === sessionGeneration.current) {
            setModelBError(privateErrorMessage(cause));
            // A failed merge waits before the next attempt instead of
            // retrying on every tick.
            if (kind === "tidying")
              nextConsolidateAt.current =
                Date.now() + 30_000 + Math.round(Math.random() * 30_000);
            // A failed claw-back retries soon (still randomized), not in 5 min.
            if (kind === "returning") {
              const retry =
                Date.now() +
                (privateWalletMode() === "single"
                  ? 20_000
                  : 45_000 + Math.round(Math.random() * 45_000));
              nextSweepAtRef.current = retry;
              setNextSweepAt(retry);
            }
          }
        } finally {
          if (backgroundYield.current === control)
            backgroundYield.current = undefined;
          busy.current = false;
          backgroundJob.current = undefined;
          setBackground(undefined);
        }
      })();
      backgroundJob.current = promise;
    },
    [modelBEnabled]
  );

  /**
   * An action (or its fee review) goes first: a running background return or
   * merge stops at its next safe point. A transaction it already sent is
   * waited for, never abandoned.
   */
  const yieldBackground = useCallback(async () => {
    const job = backgroundJob.current;
    if (!job) return;
    if (backgroundYield.current) backgroundYield.current.requested = true;
    await job.catch(() => undefined);
  }, []);

  /** Model B terms: the call's own terms plus Reserve funding of its sub. */
  const modelBTerms = useCallback(
    async (params: {
      intent: V2PrivateIntent;
      config: V2ContractConfig;
      metadata: V2RelayerMetadata;
      current: Material;
      subwallet?: string;
    }): Promise<{ base: V2FeeTerms; funding: FundingLeg[] }> => {
      const { chain } = services();
      const { intent, config, metadata, current, subwallet } = params;
      const reserve = reserveBalances(current);
      const cache = new Map<string, Promise<bigint>>();
      const present = (denom: string): Promise<bigint> => {
        if (!subwallet) return Promise.resolve(0n);
        let value = cache.get(denom);
        if (!value) {
          value = chain.bankBalance(subwallet, denom);
          cache.set(denom, value);
        }
        return value;
      };
      // Quotes are read-only; any owned sub answers the classification query.
      const querySub =
        subwallet ?? subwalletInfos(current).at(-1)?.address ?? "";
      const base = await quotePrivateFeeTerms({
        intent,
        config,
        metadata,
        // A fresh sub is empty: fee liquidity is whatever funding can bring.
        spendingBalance: async (denom) =>
          (await present(denom)) + (reserve.get(denom) ?? 0n),
        callFeeQuote: (calls) => chain.quoteCallFees(querySub, calls, config),
      });
      const strict = config.fee_rules != null;
      const held = new Map<string, bigint>();
      for (const coin of base.totalDebit)
        held.set(coin.denom, await present(coin.denom));
      const funding = planFunding({
        debit: base.totalDebit,
        present: held,
        reserve,
        feeBps: config.protocol_fee_bps,
        rounding: strict ? "ceil" : "floor",
        deducted: !strict,
        transferRelayerFee: metadata.sponsoredActions.has(
          "transfer_to_subwallet"
        )
          ? undefined
          : (denom) => {
              const amount = metadata.minimumFees.get(denom);
              if (!amount || amount <= 0n)
                throw new Error(
                  "No compatible relayer fee for this reserve transfer"
                );
              return amount;
            },
      });
      return { base, funding };
    },
    [services]
  );

  const quotePrivateFees = useCallback(
    async (message: Msg): Promise<V2PrivateFeeQuote> => {
      if (modelBEnabled) warmProofs();
      const current = material.current;
      const pending =
        current &&
        pendingRelays.current.get(
          onboardingIdentity(current.keys, current.keyContext)
        );
      if (pending)
        throw Object.assign(
          new V2RelayOutcomeUnknownError(pending.operation.txHash),
          {
            operationId: pending.operation.id,
            finalTransaction: pending.finalTransaction,
          }
        );
      // The review goes first: a background return or merge stops at its next
      // safe point (one it already sent is waited for), and no new one starts
      // while the review is open.
      reviewQuietUntil.current = Date.now() + REVIEW_QUIET_MS;
      if (busy.current && backgroundJob.current) await yieldBackground();
      if (busy.current)
        throw new Error("A private operation is already running");
      ensureRecoveryExportComplete(
        recoveryExportGate.current,
        "depositing or spending"
      );
      const intent = privateIntent(message);
      if (modelBEnabled && intent.kind === "call" && material.current) {
        const ready = () =>
          !!material.current &&
          interactionSubwallet(
            material.current,
            pinnedRef.current,
            positionsRef.current
          ) === currentFeeSelection.current.subwallet;
        if (!ready() && backgroundJob.current)
          await backgroundJob.current.catch(() => undefined);
        if (!ready()) {
          // No one-time sub yet (right after unlock or a previous action).
          // Prepare it now; the product rebuilds its message for that sub and
          // the confirmation re-quotes automatically.
          const current = material.current;
          const generation = sessionGeneration.current;
          if (
            current &&
            !interactionSubwallet(
              current,
              pinnedRef.current,
              positionsRef.current
            )
          ) {
            busy.current = true;
            setBackground("preparing");
            try {
              const rotate = () =>
                onboard(current, generation, true, 0, "rotation");
              let rotated: Material | undefined;
              try {
                rotated = await rotate();
              } catch (cause) {
                // Nothing was submitted on a preflight rejection (node lag):
                // wait one block and rebuild from fresh recovery once.
                if (!/preflight/i.test(privateErrorMessage(cause))) throw cause;
                await sleep(V2_PREFLIGHT_RETRY_DELAY_MS);
                rotated = await rotate();
              }
              if (
                rotated &&
                generation === sessionGeneration.current &&
                material.current === current
              )
                publish(rotated, isPrivateModeRef.current);
              else if (rotated) wipeKeys(rotated.keys);
            } finally {
              busy.current = false;
              setBackground(undefined);
            }
          } else if (current) publish(current, isPrivateModeRef.current);
          throw new V2FeeReviewError();
        }
      }
      const binding = feeBinding(message);
      const subwallet = currentFeeSelection.current.subwallet;
      const { chain } = services();
      const { config, metadata, terms } = await readPrivateFeeEstimate(
        async () => {
          if (binding !== feeBinding(message))
            throw new Error("Private Mode changed while estimating fees");
          const [config, metadata] = await Promise.all([
            chain.contractConfig(),
            chain.relayerMetadata(),
          ]);
          fundingBundles.current = metadata.fundingBundles === true;
          const active = material.current;
          if (modelBEnabled && intent.kind === "call" && active) {
            const { base, funding } = await modelBTerms({
              intent,
              config,
              metadata,
              current: active,
              subwallet: interactionSubwallet(
                active,
                pinnedRef.current,
                positionsRef.current
              ),
            });
            return { config, metadata, terms: { ...base, funding } };
          }
          const terms = await quotePrivateFeeTerms({
            intent,
            config,
            metadata,
            spendingBalance: (denom) => chain.bankBalance(subwallet, denom),
            callFeeQuote: (calls) =>
              chain.quoteCallFees(subwallet, calls, config),
          });
          return { config, metadata, terms };
        }
      );
      if (binding !== feeBinding(message))
        throw new Error("Private Mode changed while estimating fees");
      return feeQuotes.current.issue(
        binding,
        intent,
        privateFeePolicy(config, metadata),
        terms
      );
    },
    [
      feeBinding,
      modelBEnabled,
      modelBTerms,
      onboard,
      publish,
      services,
      warmProofs,
    ]
  );

  const executePrivateIntent = useCallback(
    async (
      message: Msg,
      reviewedQuote?: V2PrivateFeeQuote
    ): Promise<TxResult> => {
      assertSessionAccess();
      // This action owns its toast from the first frame. While a background
      // preparation, return or merge finishes first, the toast says so instead
      // of showing the result of an earlier action.
      const operationId = ++operationSequence.current;
      const startedAt = Date.now();
      let waited = false;
      if (busy.current && backgroundJob.current) {
        waited = true;
        setOperation({
          id: operationId,
          label: "Private transaction",
          phase: "preparing",
          startedAt,
          waiting: true,
        });
        await yieldBackground();
      }
      // The action cannot start after all: hand the toast back.
      const giveUp = (shown?: V2PrivateOperation): void => {
        if (waited)
          setOperation((current) =>
            current?.id === operationId ? shown : current
          );
      };
      const begin = () => {
        if (busy.current)
          throw new Error("A private operation is already running");
        if (!material.current || !selectedSubwallet || !selected)
          throw new Error("Unlock Private Mode first");
        // The last line of defence: the Spending account never pays a
        // connected wallet, whichever form built the action.
        if (
          privateActionNamesConnectedWallet(message, [
            material.current.keyContext.walletAddress,
            ...connectedAddresses.current,
          ])
        )
          throw new Error(
            "This would pay a connected wallet from your Spending account and link them on-chain. Nothing was sent."
          );
        ensureRecoveryExportComplete(
          recoveryExportGate.current,
          "depositing or spending"
        );
        const starting = material.current;
        if (!starting) throw new Error("Unlock Private Mode first");
        const identity = onboardingIdentity(starting.keys, starting.keyContext);
        const unresolved = pendingRelays.current.get(identity);
        if (unresolved) {
          giveUp(unresolved.operation);
          throw Object.assign(
            new V2RelayOutcomeUnknownError(unresolved.operation.txHash),
            {
              operationId: unresolved.operation.id,
              finalTransaction: unresolved.finalTransaction,
            }
          );
        }
        const binding = feeBinding(message);
        const reviewed = feeQuotes.current.consume(
          reviewedQuote,
          binding,
          privateIntent(message)
        );
        return { starting, identity, binding, reviewed };
      };
      let begun: ReturnType<typeof begin>;
      try {
        begun = begin();
      } catch (cause) {
        giveUp();
        throw cause;
      }
      const { starting, identity, binding, reviewed } = begun;
      // The review is over: background returns may resume after this action.
      reviewQuietUntil.current = 0;
      const generation = sessionGeneration.current;
      let operationSubwallet = currentFeeSelection.current.subwallet;
      const intent = reviewed.intent;
      const modelBCall = modelBEnabled && intent.kind === "call";
      const resultAccount = { ...message.account };
      const operationKeys = copyKeys(starting.keys);
      // Undo stays possible until the first transaction is sent. Preparation
      // (balance, fees, proofs) runs meanwhile, so the window costs no time.
      const undo: UndoWindow = {
        operationId,
        until: 0,
        cancelled: false,
        pauses: [],
      };
      const assertCurrentSession = (): void => {
        if (undo.cancelled) throw new V2OperationCancelledError();
        if (
          sessionGeneration.current !== generation ||
          binding !== feeBinding(message)
        ) {
          throw new Error("Private Mode was locked while the operation ran");
        }
      };
      let operationMaterial: Material | undefined;
      let confirmedTxHash: string | undefined;
      // Each complete history scan is bounded, not just its individual GETs.
      // A late result is read-only and must never resume this operation after
      // the deadline, publish balances, or trigger another relay submission.
      const recoverForOperation = async (
        keys: V2Keys,
        keyContext: V2KeyContext
      ): Promise<Material> => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const deadline = new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error("Private account recovery timed out.")),
            120_000
          );
        });
        try {
          return await Promise.race([recover(keys, keyContext), deadline]);
        } finally {
          clearTimeout(timer);
        }
      };
      const reportOperation = (update: Partial<V2PrivateOperation>) => {
        if (
          sessionGeneration.current !== generation &&
          !(
            expiredOperation.current?.generation === generation &&
            receiptVisible(identity)
          )
        )
          return;
        setOperation((current) =>
          current?.id === operationId ? { ...current, ...update } : current
        );
      };
      busy.current = true;
      setStatus("processing");
      refreshBlockTime();
      // A new Spending account is funded for the first time only after a
      // short random pause, and not right after a return it would mirror, so
      // its creation, an old account's return and its first funding do not
      // line up on chain. The toast says what it waits for.
      const fundingLegs =
        intent.kind === "transfer"
          ? [{ denom: intent.denom, amount: BigInt(intent.amount) }]
          : modelBCall
            ? (reviewed.terms.funding ?? []).map((leg) => ({
                denom: leg.denom,
                amount: BigInt(leg.transfer),
              }))
            : [];
      const fundingAt = modelBEnabled
        ? firstFundingNotBefore({
            hold: fundingHold.current,
            target: operationSubwallet,
            legs: fundingLegs,
            returns: recentReturns.current,
            now: Date.now(),
          })
        : undefined;
      if (fundingAt !== undefined) {
        waited = true;
        setOperation({
          id: operationId,
          label: "Private transaction",
          phase: "preparing",
          startedAt,
          waiting: true,
          waitingFor: "spending",
        });
        try {
          while (Date.now() < fundingAt) {
            await sleep(Math.min(1_000, fundingAt - Date.now()));
            assertCurrentSession();
          }
        } catch (cause) {
          busy.current = false;
          wipeKeys(operationKeys);
          if (sessionGeneration.current === generation)
            setStatus(material.current ? "ready" : "error");
          giveUp();
          throw cause;
        }
      }
      // This action makes the new account's first funding: the pause is over.
      if (
        fundingLegs.length > 0 &&
        fundingHold.current?.subwallet === operationSubwallet
      )
        fundingHold.current = undefined;
      // Undo is an opt-in device preference ("Privacy on this device").
      const undoEnabled = sessionPolicyRef.current.undoBeforeSending === true;
      const pauses = () => undo.pauses.map(([from, to]) => [from, to] as const);
      if (undoEnabled) {
        // The window starts once this action runs, after any earlier transfer.
        undo.until = Date.now() + UNDO_WINDOW_MS;
        undo.onChange = () =>
          reportOperation({
            undoUntil: undo.until,
            undoHeld: undo.heldSince !== undefined,
            undoLeftMs:
              undo.heldSince === undefined
                ? undefined
                : Math.max(undo.until - undo.heldSince, 0),
            pauses: pauses(),
          });
        undo.onCancel = () =>
          reportOperation({
            cancelled: true,
            undoUntil: undefined,
            undoHeld: undefined,
            undoLeftMs: undefined,
            pauses: pauses(),
          });
        undoWindow.current = undo;
      }
      setOperation({
        id: operationId,
        label: "Private transaction",
        phase: "preparing",
        startedAt,
        blockTimeMs: blockTimeMs.current,
        ...(undoEnabled && { undoUntil: undo.until }),
      });
      // The first network write waits for the Undo window; later ones pass.
      const awaitUndoWindow = async () => {
        if (undoWindow.current !== undo) {
          if (undo.cancelled) throw new V2OperationCancelledError();
          return;
        }
        // Wait out the window; a hover or focus holds it (wake re-checks).
        while (
          !undo.cancelled &&
          (undo.heldSince !== undefined || Date.now() < undo.until)
        ) {
          const wait =
            undo.heldSince !== undefined
              ? 60_000
              : Math.max(undo.until - Date.now(), 0);
          await new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, wait);
            undo.wake = () => {
              clearTimeout(timer);
              resolve();
            };
          });
        }
        if (undoWindow.current === undo) undoWindow.current = undefined;
        if (undo.cancelled) throw new V2OperationCancelledError();
        reportOperation({
          undoUntil: undefined,
          undoHeld: undefined,
          undoLeftMs: undefined,
          pauses: pauses(),
        });
      };
      // What the user sees instead of one long loader: every relayed
      // transaction, what it does, and its expected time (about one block).
      const stepText: Record<
        OperationStepKey,
        { label: string; detail?: string }
      > = {
        prepare: {
          label: "Getting ready",
          detail: "Checking your balance and fees",
        },
        combine: {
          label: "Combining your balance",
          detail: "Joining it into one amount first",
        },
        fund: {
          label: "Preparing your funds",
          detail: "From your private balance",
        },
        submit: {
          label:
            intent.kind === "call"
              ? describeCalls(intent.calls)
              : intent.kind === "withdraw"
                ? `Withdrawing ${denomSymbol(intent.denom)}`
                : intent.kind === "transfer"
                  ? `Moving ${denomSymbol(intent.denom)} to Spending`
                  : `Moving ${denomSymbol(intent.denom)} to your Private Reserve`,
          detail: "Sending and waiting for confirmation",
        },
        update: { label: "Updating your balance" },
      };
      // The toast shows one short line: what happens right now.
      const statusOf: Record<OperationStepKey, string> = {
        prepare: "Preparing your transaction",
        combine: "Preparing your funds",
        fund: "Preparing your funds",
        submit: "Confirming",
        update: "Updating your balance",
      };
      // Unified wallet: success shows as soon as the action is confirmed;
      // balances refresh in the background. The whole step plan is known
      // before the first step shows: the reviewed funding, the Reserve notes
      // it draws from and whether the relayer bundles funding with the action.
      // Later checks confirm it; should it change, the bar continues from
      // where it is and the time left never shows higher.
      type StepPlan = {
        keys: OperationStepKey[];
        merges: number;
        combines: { denom: string; count: number }[];
        bundled: boolean;
        /** The action's proof is built while its funding transfer lands. */
        proofReady: boolean;
      };
      const approvedFee = reviewed.terms.relayerFee;
      const approvedFeeAmount = approvedFee ? BigInt(approvedFee.amount) : 0n;
      const reserveMerges = (
        notes: readonly RecoveredNote[],
        denom: string,
        required: bigint
      ) =>
        mergesNeeded(
          notes.flatMap((note) =>
            note.note.kind === "reserve" && note.note.denom === denom
              ? [BigInt(note.note.amount)]
              : []
          ),
          required
        );
      const planSteps = (
        notes: readonly RecoveredNote[],
        legs: readonly Readonly<FundingLeg>[],
        bundles: boolean
      ): StepPlan => {
        if (!modelBCall) {
          const merges =
            intent.kind === "withdraw" || intent.kind === "transfer"
              ? reserveMerges(
                  notes,
                  intent.denom,
                  BigInt(intent.amount) + approvedFeeAmount
                )
              : 0;
          return {
            keys: [
              "prepare",
              ...(merges ? (["combine"] as const) : []),
              "submit",
              "update",
            ],
            merges,
            combines: [],
            bundled: false,
            proofReady: false,
          };
        }
        const combines = legs
          .map((leg) => ({
            denom: leg.denom,
            count: reserveMerges(
              notes,
              leg.denom,
              BigInt(leg.transfer) + BigInt(leg.relayerFee ?? "0")
            ),
          }))
          .filter((entry) => entry.count > 0);
        const merges = combines.reduce((sum, entry) => sum + entry.count, 0);
        // One transaction instead of two: the relayer sends the funding
        // transfer and the action together, atomically.
        const bundled = bundles && legs.length === 1 && merges === 0;
        return {
          keys: [
            "prepare",
            ...(merges ? (["combine"] as const) : []),
            ...(legs.length && !bundled ? (["fund"] as const) : []),
            "submit",
          ],
          merges,
          combines,
          bundled,
          // A bundle proves its funding transfer inside the step.
          proofReady: legs.length > 0 && !bundled,
        };
      };
      const samePlan = (left: StepPlan, right: StepPlan) =>
        JSON.stringify([left.keys, left.merges, left.proofReady]) ===
        JSON.stringify([right.keys, right.merges, right.proofReady]);
      let stepPlan: StepPlan | undefined;
      let plan: { key: OperationStepKey; expectedMs: number }[] = [];
      let at = -1;
      let planAt: number | undefined;
      let progressBase = 0;
      let remainingCap: number | undefined;
      const timing = new Map<
        OperationStepKey,
        { startedAt?: number; finishedAt?: number }
      >();
      const steps = (): V2PrivateOperationStep[] =>
        plan.map((step, index) => ({
          key: step.key,
          label: stepText[step.key].label,
          ...(stepText[step.key].detail && {
            detail: stepText[step.key].detail,
          }),
          state: index < at ? "done" : index === at ? "active" : "todo",
          ...timing.get(step.key),
          expectedMs: step.expectedMs,
        }));
      const applyPlan = (next: StepPlan) => {
        const now = Date.now();
        // Continue from what the toast shows right now.
        const shown = operationProgress(
          {
            startedAt,
            planAt,
            progressBase,
            remainingCap,
            steps: plan.length ? steps() : undefined,
            pauses: undo.pauses,
          },
          now
        );
        const replanning = plan.length > 0;
        const activeKey = plan[at]?.key;
        plan = next.keys.map((key) => ({
          key,
          // A step that already started keeps its estimate.
          expectedMs:
            plan.find(
              (step) =>
                step.key === key && timing.get(key)?.startedAt !== undefined
            )?.expectedMs ??
            expectedStepMs(key, blockTimeMs.current, proveMs.current, {
              proofReady: next.proofReady,
              prepareMs: prepareMs.current,
            }) * (key === "combine" ? Math.max(next.merges, 1) : 1),
        }));
        at = activeKey ? plan.findIndex((step) => step.key === activeKey) : -1;
        stepPlan = next;
        planAt = now;
        progressBase = shown.share;
        remainingCap = replanning ? shown.remainingMs : undefined;
        reportOperation({ steps: steps(), planAt, progressBase, remainingCap });
      };
      const showStep = (key: OperationStepKey | "done") => {
        const now = Date.now();
        const next =
          key === "done"
            ? plan.length
            : plan.findIndex((step) => step.key === key);
        // Steps only move forward.
        if (next < 0 || next < at) return;
        at = next;
        plan.forEach((step, index) => {
          const entry = { ...timing.get(step.key) };
          if (
            index < at &&
            entry.startedAt !== undefined &&
            entry.finishedAt === undefined
          ) {
            entry.finishedAt = now;
            // The checks before the first transaction take a similar time
            // each run: learn it for the next estimate.
            if (step.key === "prepare")
              prepareMs.current = nextProveEstimate(
                prepareMs.current,
                now - entry.startedAt
              );
          }
          if (index === at) entry.startedAt ??= now;
          timing.set(step.key, entry);
        });
        reportOperation({
          ...(key === "done"
            ? { finishedAt: now, statusText: undefined }
            : { message: stepText[key].label, statusText: statusOf[key] }),
          blockTimeMs: blockTimeMs.current,
          steps: steps(),
          planAt,
          progressBase,
          remainingCap,
        });
      };
      // What the action moves from the Reserve into the Spending account.
      let funding: readonly Readonly<FundingLeg>[] =
        reviewed.terms.funding ?? [];
      let latestFunding: readonly Readonly<FundingLeg>[] | undefined;
      // The split between the Reserve and the Spending account may change
      // before this action runs (a return to the Reserve or a payout landed
      // in between). Re-plan it, but never above the fee the user approved.
      const acceptFunding = (
        next: readonly Readonly<FundingLeg>[]
      ): readonly Readonly<FundingLeg>[] => {
        if (JSON.stringify(next) === JSON.stringify(funding)) return funding;
        const cost = (legs: readonly Readonly<FundingLeg>[]) => {
          const total = new Map<string, bigint>();
          for (const leg of legs)
            total.set(
              leg.denom,
              (total.get(leg.denom) ?? 0n) +
                BigInt(leg.fee) +
                BigInt(leg.relayerFee ?? "0")
            );
          return total;
        };
        const approved = cost(reviewed.terms.funding ?? []);
        for (const [denom, amount] of cost(next))
          if (amount > (approved.get(denom) ?? 0n))
            throw new V2FeeReviewError();
        return next;
      };
      // Without a wait the plan is known now; after one it waits for the
      // fresh balances below.
      if (!waited) {
        applyPlan(
          planSteps(
            starting.recovery.activeNotes,
            funding,
            fundingBundles.current
          )
        );
        showStep("prepare");
      }
      try {
        const { chain } = services();
        // One signer carries this whole operation: its proofs bind it.
        let metadata = chooseRelayer(await chain.relayerMetadata(), "action");
        fundingBundles.current = metadata.fundingBundles === true;
        let approvedFeePolicy: ActionFeePolicy | undefined;
        // The contract config the last fee check read (reused for planning).
        let checkedConfig: V2ContractConfig | undefined;
        const revalidateFees = async () => {
          assertCurrentSession();
          const [config, freshMetadata] = await Promise.all([
            chain.contractConfig(),
            chain.relayerMetadata(),
          ]);
          checkedConfig = config;
          assertCurrentSession();
          if (modelBCall) {
            // Funding is verified once before it runs; each later step only
            // re-checks the call's own terms (funding changes the balances).
            const { base, funding: fresh } = await modelBTerms({
              intent,
              config,
              metadata: freshMetadata,
              current: operationMaterial ?? starting,
              subwallet: operationSubwallet,
            });
            assertCurrentSession();
            latestFunding = fresh;
            const { funding: _funding, ...reviewedBase } = reviewed.terms;
            assertPrivateFeeTermsUnchanged(
              { policy: reviewed.policy, terms: reviewedBase },
              privateFeePolicy(config, freshMetadata),
              base
            );
            metadata = { ...freshMetadata, address: metadata.address };
            approvedFeePolicy = actionFeePolicy(config, base.protocolFees);
            return;
          }
          const terms = await quotePrivateFeeTerms({
            intent,
            config,
            metadata: freshMetadata,
            spendingBalance: (denom) =>
              chain.bankBalance(operationSubwallet, denom),
            callFeeQuote: (calls) =>
              chain.quoteCallFees(operationSubwallet, calls, config),
          });
          assertCurrentSession();
          assertPrivateFeeTermsUnchanged(
            reviewed,
            privateFeePolicy(config, freshMetadata),
            terms
          );
          metadata = { ...freshMetadata, address: metadata.address };
          approvedFeePolicy = actionFeePolicy(config, terms.protocolFees);
        };
        // The fee check and the fresh account scan do not depend on each
        // other: run them at the same time.
        const recovering = recoverForOperation(
          operationKeys,
          starting.keyContext
        );
        recovering.catch(() => undefined);
        await revalidateFees();
        if (!stepPlan) {
          // After a wait: plan with the balances the earlier transfer left.
          if (modelBCall && latestFunding)
            funding = acceptFunding(latestFunding);
          applyPlan(
            planSteps(
              starting.recovery.activeNotes,
              funding,
              metadata.fundingBundles === true
            )
          );
          showStep("prepare");
        }
        let proofGenerator: ProofGenerator | undefined;
        let current = await recovering;
        operationMaterial = current;
        assertCurrentSession();

        const awaitInclusion = async (
          prepared: PreparedV2Execution,
          hash: string
        ): Promise<string | undefined> => {
          const until = Date.now() + INCLUSION_WAIT_MS;
          while (Date.now() < until) {
            await sleep(3_000);
            assertCurrentSession();
            const outcome = await chain
              .reconcileRelay(prepared, metadata, hash)
              .catch(() => undefined);
            if (outcome?.status === "confirmed") return outcome.txHash;
            if (outcome?.status === "failed")
              throw new Error(
                `${outcome.message} Nothing else was sent. Review and try again.`
              );
          }
          return undefined;
        };
        const relay = async (
          prepare: () => Promise<PreparedV2Execution>,
          finalTransaction = false,
          // A funding transfer does not change the sub's control note, and its
          // pre-transfer root stays in the contract's root history: the next
          // step needs no fresh recovery in between.
          refreshAfter = true
        ): Promise<string> => {
          const hash = await retryV2StaleRootOnce(
            async () => {
              assertCurrentSession();
              const prepared = await prepare();
              assertCurrentSession();
              // Proof generation may take time. Never silently accept a changed
              // fee policy, relayer, or depleted fee balance before submission.
              await revalidateFees();
              await awaitUndoWindow();
              assertCurrentSession();
              carryPrivacyLimits(prepared, current);
              if (finalTransaction) reportOperation({ phase: "submitting" });
              const receipt: PendingPrivateRelay = {
                // Never retain prepared.outputs: these are decrypted notes.
                // Reconciliation needs only the exact public wire message.
                prepared: {
                  msg: JSON.parse(JSON.stringify(prepared.msg)),
                  funds: prepared.funds.map((coin) => ({ ...coin })),
                  outputs: [],
                  consumedNullifiers: [],
                  ...(prepared.bundle && {
                    bundle: JSON.parse(JSON.stringify(prepared.bundle)),
                  }),
                },
                metadata: {
                  ...metadata,
                  minimumFees: new Map(metadata.minimumFees),
                  sponsoredActions: new Set(metadata.sponsoredActions),
                },
                finalTransaction,
                checking: false,
                operation: {
                  id: operationId,
                  label: "Private transaction",
                  phase: "submitting",
                  startedAt,
                  finalTransaction,
                },
              };
              pendingRelays.current.set(identity, receipt);
              try {
                const hash = await relayResubmittingOnPreflight(() =>
                  chain.relay(prepared, metadata)
                ).catch(async (cause) => {
                  // A preparatory step the relayer could not confirm in time
                  // is usually a slow block. Nothing is sent again: read the
                  // chain until it lands, then continue the confirmed action.
                  if (
                    !modelBEnabled ||
                    finalTransaction ||
                    !isV2RelayOutcomeUnknownError(cause) ||
                    !cause.transactionHash
                  )
                    throw cause;
                  reportOperation({ statusText: "Waiting for the network" });
                  const settled = await awaitInclusion(
                    prepared,
                    cause.transactionHash
                  );
                  if (!settled) throw cause;
                  return settled;
                });
                pendingRelays.current.delete(identity);
                if (
                  sessionGeneration.current !== generation &&
                  receiptVisible(identity)
                ) {
                  setOperation({
                    ...receipt.operation,
                    phase: "confirmed",
                    txHash: hash,
                  });
                }
                return hash;
              } catch (cause) {
                if (isV2RelayOutcomeUnknownError(cause)) {
                  Object.assign(cause, { operationId, finalTransaction });
                  receipt.operation = {
                    ...receipt.operation,
                    phase: "pending",
                    txHash: cause.transactionHash,
                    message:
                      "Confirmation is pending. Check status before submitting again.",
                  };
                } else {
                  pendingRelays.current.delete(identity);
                }
                if (
                  sessionGeneration.current !== generation &&
                  receiptVisible(identity)
                ) {
                  setOperation(
                    isV2RelayOutcomeUnknownError(cause)
                      ? receipt.operation
                      : {
                          ...receipt.operation,
                          phase: "failed",
                          message: "Review before trying again.",
                        }
                  );
                }
                throw cause;
              }
            },
            async () => {
              assertCurrentSession();
              current = await recoverForOperation(
                current.keys,
                current.keyContext
              );
              operationMaterial = current;
              assertCurrentSession();
            },
            0
          );
          if (finalTransaction) {
            // Record the confirmed hash before recovery can fail. Never turn a
            // completed payment into a retryable failed action because its
            // subsequent balance refresh was unavailable.
            confirmedTxHash = hash;
            reportOperation({
              phase: "confirmed",
              txHash: hash,
              message: "Transaction confirmed. Refreshing balances…",
            });
            showStep("update");
          }
          assertCurrentSession();
          if (!refreshAfter) {
            operationMaterial = current;
            return hash;
          }
          try {
            current = await recoverForOperation(
              current.keys,
              current.keyContext
            );
          } catch (cause) {
            if (finalTransaction) throw cause;
            // This confirmed only the preparatory merge. Do not put its hash
            // into the final action's receipt or resolve that action as success.
            throw Object.assign(
              new Error(
                "Your transaction has not been submitted. Refresh your balance and confirm again."
              ),
              { preparationTransactionHash: hash, finalTransaction: false }
            );
          }
          operationMaterial = current;
          assertCurrentSession();
          return hash;
        };
        const context = async () => {
          proofGenerator ??= await prove();
          assertCurrentSession();
          const action = await actionContext(metadata, proofGenerator, current);
          // Undo stops before the proof, not after it.
          assertCurrentSession();
          return {
            ...action,
            ...(approvedFeePolicy && { feePolicy: approvedFeePolicy }),
          };
        };
        const control = (): RecoveredNote => {
          const note = activeControlNotes(current).find(
            (candidate) =>
              (candidate.note as ControlNote).subwallet === operationSubwallet
          );
          if (!note)
            throw new Error(
              "The selected subwallet has no active control note"
            );
          return note;
        };
        const reserve = async (
          denom: string,
          required: bigint
        ): Promise<RecoveredNote> => {
          for (let attempts = 0; attempts < 32; attempts += 1) {
            const notes = current.recovery.activeNotes
              .filter(
                (
                  candidate
                ): candidate is RecoveredNote & { note: ReserveNote } =>
                  candidate.note.kind === "reserve" &&
                  candidate.note.denom === denom
              )
              .sort((left, right) =>
                Number(BigInt(right.note.amount) - BigInt(left.note.amount))
              );
            const sufficient = notes.find(
              (note) => BigInt(note.note.amount) >= required
            );
            if (sufficient) return sufficient;
            const total = notes.reduce(
              (sum, note) => sum + BigInt(note.note.amount),
              0n
            );
            if (total < required)
              throw new Error(`Insufficient ${denomSymbol(denom)} balance`);
            if (
              notes.length < 2 ||
              !metadata.sponsoredActions.has("merge_reserve_notes")
            ) {
              if (notes.length < 2) {
                throw new Error("Private reserve consolidation is unavailable");
              }
              throw new Error(
                "Private reserve consolidation requires a sponsored relayer. No public-wallet transaction was submitted."
              );
            }
            const firstNullifier = notes[0].nullifier;
            const secondNullifier = notes[1].nullifier;
            await relay(async () => {
              const active = current.recovery.activeNotes;
              const first = active.find(
                (note) => note.nullifier === firstNullifier
              );
              const second = active.find(
                (note) => note.nullifier === secondNullifier
              );
              if (!first || !second) {
                throw new Error(
                  "Private reserve changed while rebuilding its proof"
                );
              }
              const proving = await context();
              return measureProof(proveMs, () =>
                prepareReserveMerge(proving, {
                  root: current.recovery.root,
                  first,
                  second,
                })
              );
            });
          }
          throw new Error(
            "Private reserve consolidation exceeded its safety bound"
          );
        };

        let prepareExecution: () => Promise<PreparedV2Execution>;
        if (intent.kind === "withdraw" || intent.kind === "transfer") {
          prepareExecution = async () => {
            const input = await reserve(
              intent.denom,
              BigInt(intent.amount) + approvedFeeAmount
            );
            // Any merges are done: the action itself runs now.
            showStep("submit");
            const params = {
              root: current.recovery.root,
              input,
              spendAmount: BigInt(intent.amount),
              relayerFee: approvedFeeAmount,
            };
            const proving = await context();
            return measureProof(proveMs, () =>
              intent.kind === "withdraw"
                ? prepareReserveWithdraw(proving, {
                    ...params,
                    recipient: intent.recipient,
                  })
                : prepareReserveToSubwallet(proving, {
                    ...params,
                    subwallet: operationSubwallet,
                  })
            );
          };
        } else if (intent.kind === "shield") {
          prepareExecution = async () => {
            const proving = await context();
            return measureProof(proveMs, () =>
              prepareShieldFromSubwallet(proving, {
                root: current.recovery.root,
                input: control(),
                subwallet: operationSubwallet,
                amount: { denom: intent.denom, amount: intent.amount },
              })
            );
          };
        } else {
          prepareExecution = async () => {
            const proving = await context();
            return measureProof(proveMs, () =>
              prepareSubwalletCall(proving, {
                root: current.recovery.root,
                input: control(),
                subwallet: operationSubwallet,
                calls: intent.calls,
                relayerFee: approvedFee,
              })
            );
          };
        }
        // Model B: fund the one-time sub from the Reserve, proving the action
        // itself while the funding transfer waits for its block.
        let earlyCall: Promise<PreparedV2Execution> | undefined;
        let bundleTransfer: (() => Promise<PreparedV2Execution>) | undefined;
        let bundledMove: PendingMove | undefined;
        if (modelBCall) {
          // One sub per interaction: the managed position's sub, else a fresh
          // one-time sub. The reviewed message was built for exactly this sub
          // (the fee binding pins it); anything else requires a fresh review.
          const target = interactionSubwallet(
            current,
            pinnedRef.current,
            positionsRef.current
          );
          if (!target || target !== operationSubwallet)
            throw new V2FeeReviewError();
          operationSubwallet = target;
          const config = checkedConfig ?? (await chain.contractConfig());
          assertCurrentSession();
          const { funding: fresh } = await modelBTerms({
            intent,
            config,
            metadata,
            current,
            subwallet: operationSubwallet,
          });
          assertCurrentSession();
          funding = acceptFunding(fresh);
        }
        // The fresh balances confirm the plan shown so far; should they not,
        // the new plan continues from where the bar is.
        const confirmed = planSteps(
          current.recovery.activeNotes,
          funding,
          metadata.fundingBundles === true
        );
        if (!stepPlan || !samePlan(confirmed, stepPlan)) applyPlan(confirmed);
        else stepPlan = confirmed;
        const { merges, combines, bundled } = confirmed;
        if (modelBCall) {
          const needsCombine = merges > 0;
          if (needsCombine)
            stepText.combine.detail = `Joining your ${combines
              .map((entry) => denomSymbol(entry.denom))
              .join(" and ")} into one amount first`;
          stepText.fund.detail = `${formatCoins(
            funding.map((leg) => ({ denom: leg.denom, amount: leg.transfer }))
          )} from your private balance`;
          if (funding.length) {
            // The action's own proof needs nothing from the funding: build it
            // now, in parallel with combining and moving the funds.
            earlyCall = prepareExecution();
            earlyCall.catch(() => undefined);
            if (!bundled) showStep(needsCombine ? "combine" : "fund");
          }
          if (bundled) {
            const [leg] = funding;
            const relayerFee = BigInt(leg.relayerFee ?? "0");
            bundleTransfer = async () => {
              const proving = await context();
              const params = {
                root: current.recovery.root,
                input: await reserve(
                  leg.denom,
                  BigInt(leg.transfer) + relayerFee
                ),
                spendAmount: BigInt(leg.transfer),
                relayerFee,
                subwallet: operationSubwallet,
              };
              return measureProof(proveMs, () =>
                prepareReserveToSubwallet(proving, params)
              );
            };
            bundledMove = {
              denom: leg.denom,
              direction: "fund",
              subBefore: BigInt(leg.present),
              subDelta: BigInt(leg.needed) - BigInt(leg.present),
              reserveDelta: BigInt(leg.transfer) + relayerFee,
            };
          }
          for (const leg of bundled ? [] : funding) {
            const relayerFee = BigInt(leg.relayerFee ?? "0");
            const recordMove = () =>
              setPendingMoves((moves) => [
                ...moves,
                {
                  denom: leg.denom,
                  direction: "fund",
                  subBefore: BigInt(leg.present),
                  subDelta: BigInt(leg.needed) - BigInt(leg.present),
                  reserveDelta: BigInt(leg.transfer) + relayerFee,
                },
              ]);
            await relay(
              async () => {
                // Select (and, if needed, consolidate) the Reserve note first:
                // a merge replaces the tree, so the root must be read after
                // it, from the recovery that produced the note's Merkle path.
                const input = await reserve(
                  leg.denom,
                  BigInt(leg.transfer) + relayerFee
                );
                showStep("fund");
                const proving = await context();
                return measureProof(proveMs, () =>
                  prepareReserveToSubwallet(proving, {
                    root: current.recovery.root,
                    input,
                    spendAmount: BigInt(leg.transfer),
                    relayerFee,
                    subwallet: operationSubwallet,
                  })
                );
              },
              false,
              false
            );
            recordMove();
          }
        }

        // Merges for a Reserve transfer run inside its step (see above).
        showStep(!modelBCall && merges > 0 ? "combine" : "submit");
        const txHash = await relay(
          async () => {
            // Use the proof built during funding once; a retry rebuilds it.
            const early = earlyCall;
            earlyCall = undefined;
            const action = early
              ? early.catch(() => prepareExecution())
              : prepareExecution();
            if (!bundleTransfer) return action;
            // Both proofs against the same root, built in parallel.
            const [transfer, call] = await Promise.all([
              bundleTransfer(),
              action,
            ]);
            return { ...call, bundle: [transfer.msg, call.msg] };
          },
          true,
          !modelBCall
        );
        assertCurrentSession();
        // Funding and action landed together: the Reserve side is final.
        if (bundledMove) {
          const move = bundledMove;
          setPendingMoves((moves) => [...moves, { ...move, settled: true }]);
        }
        if (modelBCall && intent.kind === "call") {
          // A swap's sub is never reused. A position keeps its sub pinned so
          // the page shows it; leaving that page returns to a fresh sub.
          // Single mode: one Spending sub for everything, nothing to pin.
          const keep =
            privateWalletMode() === "single" || isOneShotIntent(intent.calls)
              ? undefined
              : operationSubwallet;
          pinnedRef.current = keep;
          setPinnedSubwallet(keep);
          setPinnedPath(keep ? window.location.pathname : undefined);
          if (keep) {
            const here = sectionOf(window.location.pathname);
            const existing = positionsRef.current.find(
              (entry) => entry.subwallet === keep
            );
            // Managing from the portfolio keeps the product scope it opened in.
            const next = upsertPosition(positionsRef.current, {
              subwallet: keep,
              section:
                existing &&
                (!here.includes("/") || here.startsWith("portfolio"))
                  ? existing.section
                  : here,
            });
            positionsRef.current = next;
            setPositions(next);
            // Copy: the operation's key buffers may be wiped before this lands.
            const scanKey = current.keys.scanKey.slice();
            void savePositions(scanKey, current.keyContext, next).finally(() =>
              scanKey.fill(0)
            );
          }
          // The funds left the Reserve for good; show that until the
          // background refresh publishes the new Reserve.
          setPendingMoves((moves) =>
            moves.map((move) =>
              move.direction === "fund" ? { ...move, settled: true } : move
            )
          );
          scheduleSweep();
          void readSubBalances([operationSubwallet]).catch(() => undefined);
          const refreshKeys = copyKeys(current.keys);
          void recover(refreshKeys, current.keyContext)
            .then((fresh) => {
              // A newer operation publishes its own state; never overwrite it.
              // Keep the view the user is in: a refresh that lands after a
              // switch to the public view must not turn Private Mode back on.
              if (generation === sessionGeneration.current && !busy.current)
                publish(fresh, isPrivateModeRef.current);
              else wipeKeys(fresh.keys);
            })
            .catch(() => wipeKeys(refreshKeys));
        } else publish(current, isPrivateModeRef.current);
        showStep("done");
        reportOperation({ phase: "confirmed", message: undefined });
        return {
          address: resultAccount.address,
          network: resultAccount.network,
          txHash,
          deposited: null,
        };
      } catch (cause) {
        if (cause instanceof V2OperationCancelledError) {
          if (sessionGeneration.current === generation) {
            setError(undefined);
            setStatus(material.current ? "ready" : "error");
          }
          reportOperation({
            phase: "failed",
            cancelled: true,
            undoUntil: undefined,
            undoHeld: undefined,
            undoLeftMs: undefined,
            message: cause.message,
          });
          throw cause;
        }
        if (isV2RelayOutcomeUnknownError(cause)) {
          if (sessionGeneration.current === generation) {
            setError(undefined);
            setStatus(material.current ? "ready" : "error");
            const pending = pendingRelays.current.get(identity);
            // The toast keeps its title, steps and bar while it waits for
            // the confirmation.
            if (pending)
              setOperation((shown) =>
                shown?.id === pending.operation.id
                  ? { ...shown, ...pending.operation }
                  : pending.operation
              );
          }
          throw cause;
        }
        const message = cause instanceof Error ? cause.message : String(cause);
        if (sessionGeneration.current === generation) {
          setError(
            confirmedTxHash
              ? "Transaction confirmed. Account refresh is unavailable; refresh balances before continuing."
              : message
          );
          setStatus(material.current ? "ready" : "error");
        }
        if (confirmedTxHash) {
          reportOperation({
            phase: "confirmed",
            txHash: confirmedTxHash,
            message: "Transaction confirmed. Balances are still updating.",
          });
          return {
            address: resultAccount.address,
            network: resultAccount.network,
            txHash: confirmedTxHash,
            deposited: null,
          };
        }
        reportOperation({
          phase: "failed",
          message:
            cause !== null &&
            typeof cause === "object" &&
            "preparationTransactionHash" in cause
              ? message
              : "Check the transaction result before trying again.",
        });
        throw cause;
      } finally {
        if (undoWindow.current === undo) undoWindow.current = undefined;
        if (operationMaterial && material.current !== operationMaterial) {
          wipeKeys(operationMaterial.keys);
        } else if (!operationMaterial) {
          wipeKeys(operationKeys);
        }
        busy.current = false;
      }
    },
    [
      actionContext,
      assertSessionAccess,
      carryPrivacyLimits,
      feeBinding,
      prove,
      publish,
      recover,
      receiptVisible,
      selected,
      selectedSubwallet,
      services,
      modelBEnabled,
      modelBTerms,
      refreshBlockTime,
      scheduleSweep,
      readSubBalances,
    ]
  );

  /** Pool refill: prepare the next one-time sub while the user is idle. */
  const preparePool = useCallback(
    async (generation: number) => {
      const current = material.current;
      if (!current || subwalletInfos(current).some((s) => s.role === "fresh"))
        return;
      const rotated = await onboard(current, generation, true, 0, "rotation");
      if (!rotated) return;
      if (
        generation !== sessionGeneration.current ||
        material.current !== current
      ) {
        wipeKeys(rotated.keys);
        return;
      }
      publish(rotated, isPrivateModeRef.current);
    },
    [onboard, publish]
  );

  /**
   * Claw-back: every settled, non-dust bank balance in any owned sub returns
   * to the Reserve as a fresh shielded note. Staked funds, open orders and
   * loan collateral live in product contracts, not in the sub's bank balance,
   * so they stay until they pay out. Returns are staggered, never batched.
   */
  const sweepJob = useCallback(
    async (generation: number, checkpoint: () => void = () => undefined) => {
      const start = material.current;
      if (!start) return;
      const balances = await readSubBalances();
      if (generation !== sessionGeneration.current) return;
      checkpoint();
      const candidates = planSweep({
        balances,
        busy: new Set(),
        isDust: dustPolicy.current,
      });
      if (candidates.length === 0) return;
      const { chain } = services();
      const [config, relayer] = await Promise.all([
        chain.contractConfig(),
        chain.relayerMetadata(),
      ]);
      const metadata = chooseRelayer(relayer, "background");
      if (!metadata.sponsoredActions.has("shield_from_subwallet"))
        throw new Error(
          "The relayer does not sponsor returns to the Private Reserve."
        );
      const keys = copyKeys(start.keys);
      let working: Material | undefined;
      let proofGenerator: ProofGenerator | undefined;
      const assertCurrent = () => {
        if (
          generation !== sessionGeneration.current ||
          material.current !== start
        )
          throw new Error("Private Mode changed while returning funds");
      };
      try {
        working = await recover(keys, start.keyContext);
        checkpoint();
        const override = sweepDelayOverride();
        const single = privateWalletMode() === "single";
        const controlOf = (active: Material, subwallet: string) =>
          activeControlNotes(active).find(
            (note) => (note.note as ControlNote).subwallet === subwallet
          );
        for (const [position, candidate] of candidates.entries()) {
          // Single mode returns coin after coin; rotate mode staggers them.
          if (position > 0 && !single)
            await sleep(
              override !== undefined
                ? override
                : 20_000 + Math.round(Math.random() * 40_000)
            );
          assertCurrent();
          checkpoint();
          let spentControl: string | undefined;
          await retryV2StaleRootOnce(
            async () => {
              const active = working as Material;
              const control = controlOf(active, candidate.subwallet);
              if (!control) throw new Error("Owned sub-wallet note is missing");
              spentControl = control.nullifier;
              checkpoint();
              proofGenerator ??= await prove();
              assertCurrent();
              const policy = actionFeePolicy(config, []);
              const proving = {
                ...(await actionContext(metadata, proofGenerator, active)),
                ...(policy ? { feePolicy: policy } : {}),
              };
              const prepared = await measureProof(proveMs, () =>
                prepareShieldFromSubwallet(proving, {
                  root: active.recovery.root,
                  input: control,
                  subwallet: candidate.subwallet,
                  amount: candidate.coin,
                })
              );
              assertCurrent();
              // Last safe point: past here the return is sent and awaited.
              checkpoint();
              carryPrivacyLimits(prepared, active);
              return relayResubmittingOnPreflight(() =>
                chain.relay(prepared, metadata)
              );
            },
            async () => {
              working = await recover(keys, start.keyContext);
            }
          );
          // Until the Reserve is republished, show the returned coin there
          // as soon as the sub no longer shows it (no dip in the total).
          if (generation === sessionGeneration.current) {
            const amount = BigInt(candidate.coin.amount);
            // A new Spending account's first funding never mirrors it.
            const at = Date.now();
            recentReturns.current = [
              ...recentReturns.current.filter(
                (entry) => at - entry.at < RECENT_RETURN_MS
              ),
              {
                subwallet: candidate.subwallet,
                denom: candidate.coin.denom,
                amount,
                at,
                gapMs: mirrorGapMs(),
              },
            ].slice(-32);
            setPendingMoves((moves) => [
              ...moves,
              {
                denom: candidate.coin.denom,
                direction: "return",
                subBefore: amount,
                subDelta: amount,
                reserveDelta: amount,
              },
            ]);
          }
          // The next coin needs the new control note: wait until the read
          // node has seen this return instead of proving on a spent note.
          for (let attempt = 0; attempt < 4; attempt += 1) {
            working = await recover(keys, start.keyContext);
            if (
              controlOf(working, candidate.subwallet)?.nullifier !==
              spentControl
            )
              break;
            await sleep(1_500);
          }
        }
        assertCurrent();
        // Sub balances first, then the Reserve: the merged total never counts
        // a returned coin twice.
        await readSubBalances().catch(() => undefined);
        publish(working, isPrivateModeRef.current);
        working = undefined;
        // Merge the returned note right away, so the next action needs no
        // "combine" transaction.
        if (single) nextConsolidateAt.current = 0;
      } finally {
        if (working) wipeKeys(working.keys);
        else if (material.current?.keys !== keys) wipeKeys(keys);
      }
    },
    [
      actionContext,
      carryPrivacyLimits,
      prove,
      publish,
      readSubBalances,
      recover,
      services,
    ]
  );

  /**
   * Reserve housekeeping: every claw-back lands as its own note, so the
   * Reserve fragments. Merge the two smallest notes of a denom while idle, so
   * an action never has to consolidate first. Merges stay inside the pool.
   */
  const consolidateJob = useCallback(
    async (generation: number, checkpoint: () => void = () => undefined) => {
      const start = material.current;
      if (!start) return;
      const pair = reserveMergePair(start);
      if (!pair) return;
      const { chain } = services();
      const [config, relayer] = await Promise.all([
        chain.contractConfig(),
        chain.relayerMetadata(),
      ]);
      const metadata = chooseRelayer(relayer, "background");
      if (!metadata.sponsoredActions.has("merge_reserve_notes")) return;
      const keys = copyKeys(start.keys);
      let working: Material | undefined;
      let proofGenerator: ProofGenerator | undefined;
      const assertCurrent = () => {
        if (
          generation !== sessionGeneration.current ||
          material.current !== start
        )
          throw new Error("Private Mode changed while tidying the Reserve");
      };
      try {
        working = await recover(keys, start.keyContext);
        checkpoint();
        await retryV2StaleRootOnce(
          async () => {
            const active = working as Material;
            const first = active.recovery.activeNotes.find(
              (note) => note.nullifier === pair[0]
            );
            const second = active.recovery.activeNotes.find(
              (note) => note.nullifier === pair[1]
            );
            if (!first || !second) throw new Error("Reserve changed");
            checkpoint();
            proofGenerator ??= await prove();
            assertCurrent();
            const policy = actionFeePolicy(config, []);
            const proving = {
              ...(await actionContext(metadata, proofGenerator, active)),
              ...(policy ? { feePolicy: policy } : {}),
            };
            const prepared = await measureProof(proveMs, () =>
              prepareReserveMerge(proving, {
                root: active.recovery.root,
                first,
                second,
              })
            );
            assertCurrent();
            // Last safe point: past here the merge is sent and awaited.
            checkpoint();
            carryPrivacyLimits(prepared, active);
            return relayResubmittingOnPreflight(() =>
              chain.relay(prepared, metadata)
            );
          },
          async () => {
            working = await recover(keys, start.keyContext);
          }
        );
        // Publish only a Reserve that already shows the merged note.
        for (let attempt = 0; attempt < 4; attempt += 1) {
          working = await recover(keys, start.keyContext);
          if (
            !working.recovery.activeNotes.some(
              (note) => note.nullifier === pair[0] || note.nullifier === pair[1]
            )
          )
            break;
          await sleep(1_500);
        }
        assertCurrent();
        publish(working, isPrivateModeRef.current);
        working = undefined;
      } finally {
        if (working) wipeKeys(working.keys);
        else if (material.current?.keys !== keys) wipeKeys(keys);
      }
    },
    [actionContext, carryPrivacyLimits, prove, publish, recover, services]
  );

  const jobs = useRef({ preparePool, sweepJob, consolidateJob });
  jobs.current = { preparePool, sweepJob, consolidateJob };

  // First claw-back after unlock (catch-up for payouts that settled while
  // offline), then a slow periodic one.
  useEffect(() => {
    if (!modelBEnabled) return;
    if (status === "ready" && nextSweepAtRef.current === undefined) {
      scheduleSweep(false);
      void readSubBalances().catch(() => undefined);
      // Informational reads: a failure (even a synchronous one) never
      // disturbs the unlock.
      void Promise.resolve()
        .then(() => services().chain.contractConfig())
        .then((config) => setFeeBps(config.protocol_fee_bps))
        .catch(() => undefined);
      refreshBlockTime();
    }
    if (status === "locked" || status === "disabled") {
      nextSweepAtRef.current = undefined;
      setNextSweepAt(undefined);
      pinnedRef.current = undefined;
      setPinnedSubwallet(undefined);
      setPinnedPath(undefined);
      setSubBalances(new Map());
      positionsRef.current = [];
      setPositions([]);
      // The pause between new Spending accounts stays; a message does not.
      setRegeneration((state) =>
        state.error === undefined
          ? state
          : {
              running: state.running,
              ...(state.availableAt !== undefined && {
                availableAt: state.availableAt,
              }),
            }
      );
    }
  }, [
    modelBEnabled,
    readSubBalances,
    refreshBlockTime,
    scheduleSweep,
    services,
    status,
  ]);

  /**
   * Single mode: earlier Spending accounts are position accounts, so their
   * open positions stay visible and manageable. Derived from chain data and
   * the keys: a new Spending account created on another device is covered
   * too. Only once the sealed list of this identity has loaded (never
   * overwrite it with a partial one).
   */
  const rememberEarlierSpending = useCallback(
    (current: Material) => {
      if (!modelBEnabled || privateWalletMode() !== "single") return;
      if (
        positionsLoaded.current !==
        onboardingIdentity(current.keys, current.keyContext)
      )
        return;
      const subwallets = subwalletInfos(current);
      // A sub that never acted holds no position.
      const used = new Set(
        subwallets
          .filter((info) => info.role === "used")
          .map((info) => info.address)
      );
      const known = new Set(
        positionsRef.current.map((entry) => entry.subwallet)
      );
      const missing = earlierSpendingAccounts(
        subwallets,
        current.spending
      ).filter((address) => used.has(address) && !known.has(address));
      if (missing.length === 0) return;
      const next = [
        ...positionsRef.current,
        ...missing.map((subwallet) => ({ subwallet, section: "" })),
      ];
      positionsRef.current = next;
      setPositions(next);
      // Copy: the material's key buffers may be wiped before this lands.
      const scanKey = current.keys.scanKey.slice();
      void savePositions(scanKey, current.keyContext, next).finally(() =>
        scanKey.fill(0)
      );
    },
    [modelBEnabled]
  );

  // Load the sealed position registry once per unlocked identity. The first
  // Model B session adopts the subs that already exist (pre-rotation account).
  useEffect(() => {
    const current = material.current;
    if (!modelBEnabled || !current) {
      if (!current) {
        positionsIdentity.current = undefined;
        positionsLoaded.current = undefined;
      }
      return;
    }
    const identity = onboardingIdentity(current.keys, current.keyContext);
    if (positionsIdentity.current === identity) {
      rememberEarlierSpending(current);
      return;
    }
    positionsIdentity.current = identity;
    positionsLoaded.current = undefined;
    const scanKey = current.keys.scanKey.slice();
    const context = current.keyContext;
    const generation = sessionGeneration.current;
    void (async () => {
      try {
        let stored = await loadPositions(scanKey, context);
        if (stored === undefined) {
          stored = subwalletInfos(current)
            .filter((info) => info.role === "used")
            .map((info) => ({ subwallet: info.address, section: "" }));
          await savePositions(scanKey, context, stored);
        }
        if (generation !== sessionGeneration.current) return;
        const owned = new Set(
          subwalletInfos(material.current).map((info) => info.address)
        );
        const known = stored.filter((entry) => owned.has(entry.subwallet));
        positionsRef.current = known;
        setPositions(known);
        positionsLoaded.current = identity;
        const latest = material.current;
        if (
          latest &&
          onboardingIdentity(latest.keys, latest.keyContext) === identity
        )
          rememberEarlierSpending(latest);
      } finally {
        scanKey.fill(0);
      }
    })();
  }, [modelBEnabled, rememberEarlierSpending, revision]);

  useEffect(() => {
    if (!modelBEnabled) return;
    const tick = () => {
      const current = material.current;
      if (
        !current ||
        statusRef.current !== "ready" ||
        busy.current ||
        backgroundJob.current ||
        !isPrivateModeRef.current ||
        Date.now() < reviewQuietUntil.current
      )
        return;
      if (
        privateWalletMode() === "rotate" &&
        !subwalletInfos(current).some((s) => s.role === "fresh")
      ) {
        runBackground("preparing", jobs.current.preparePool);
        return;
      }
      const single = privateWalletMode() === "single";
      const due = nextSweepAtRef.current;
      if (due !== undefined && Date.now() >= due) {
        if (single) {
          // Every block while watching, then once a minute. A cheap balance
          // read first: only a returnable balance takes the operation lock.
          nextSweepAtRef.current =
            Date.now() +
            (Date.now() < watchUntil.current
              ? blockTimeMs.current
              : SWEEP_IDLE_CHECK_MS);
          if (!sweepCheck.current) {
            sweepCheck.current = true;
            void readSubBalances()
              .then((balances) => {
                if (
                  planSweep({
                    balances,
                    busy: new Set(),
                    isDust: dustPolicy.current,
                  }).length > 0
                )
                  runBackground("returning", jobs.current.sweepJob);
              })
              .catch(() => undefined)
              .finally(() => {
                sweepCheck.current = false;
              });
          }
          return;
        }
        const next = Date.now() + 5 * 60_000;
        nextSweepAtRef.current = next;
        setNextSweepAt(next);
        runBackground("returning", jobs.current.sweepJob);
        return;
      }
      if (
        Date.now() >= nextConsolidateAt.current &&
        reserveMergePair(current)
      ) {
        // Single mode merges back-to-back so the next action finds one note;
        // rotate mode spaces merges so they never land in a predictable rhythm.
        nextConsolidateAt.current = single
          ? Date.now()
          : Date.now() + 20_000 + Math.round(Math.random() * 40_000);
        runBackground("tidying", jobs.current.consolidateJob);
      }
    };
    const timer = setInterval(tick, 3_000);
    return () => clearInterval(timer);
  }, [modelBEnabled, readSubBalances, runBackground]);

  const pin = useCallback(
    (subwallet?: string) => {
      const current = material.current;
      if (
        subwallet &&
        (!current ||
          !subwalletInfos(current).some((s) => s.address === subwallet))
      )
        throw new Error("Unknown private position");
      pinnedRef.current = subwallet;
      setPinnedSubwallet(subwallet);
      setPinnedPath(subwallet ? window.location.pathname : undefined);
      if (!current) return;
      const next = interactionSubwallet(
        current,
        subwallet,
        positionsRef.current
      );
      if (next && next !== currentFeeSelection.current.subwallet) {
        setSelectedSubwallet(next);
        setOperation(undefined);
        redacted.setSubWalletAddress(next);
      }
    },
    [redacted]
  );

  const sweepNow = useCallback(() => {
    nextSweepAtRef.current = Date.now();
    setNextSweepAt(Date.now());
  }, []);

  /**
   * Single mode: a new Spending account for every new action. One sponsored
   * registration and one sponsored creation through the rotation path, the
   * salt derived from the keys so every device continues in the new sub. The
   * previous Spending sub stays a position account: its positions remain
   * visible and manageable, its free funds keep returning to the Reserve.
   */
  const regenerate = useCallback(async () => {
    assertSessionAccess();
    const current = material.current;
    const availability = regenerateAvailability({
      now: Date.now(),
      ready:
        modelBEnabled &&
        privateWalletMode() === "single" &&
        !!current &&
        statusRef.current === "ready",
      // A background return or merge is not in the way: it yields below.
      busy:
        (busy.current && !backgroundJob.current) ||
        (!!current &&
          pendingRelays.current.has(
            onboardingIdentity(current.keys, current.keyContext)
          )),
      running: regenerating.current,
      settledAt: regenerateSettledAt.current,
    });
    if (!availability.allowed) throw new Error(regenerateRefusal(availability));
    // Claimed before the first wait: a second click cannot start another.
    regenerating.current = true;
    const generation = sessionGeneration.current;
    let started = false;
    let failure: string | undefined;
    try {
      // One a day: the latest height against the current Spending account's
      // creation block (the same public height read every client makes).
      const nextHeight =
        current &&
        nextRegenerateHeight(current.spending, current.recovery.notes);
      if (nextHeight !== undefined) {
        const daily = regenerateAvailability({
          now: Date.now(),
          ready: true,
          busy: false,
          running: false,
          daily: {
            height: Number(await services().chain.height()),
            nextHeight,
            blockTimeMs: blockTimeMs.current,
          },
        });
        dailyAvailableAt.current = daily.allowed ? undefined : daily.retryAt;
        if (!daily.allowed) throw new Error(regenerateRefusal(daily));
      }
      // A running return or merge stops at its next safe point first.
      if (busy.current && backgroundJob.current) await yieldBackground();
      if (
        busy.current ||
        !current ||
        material.current !== current ||
        generation !== sessionGeneration.current
      )
        throw new Error(regenerateRefusal({ allowed: false, reason: "busy" }));
      busy.current = true;
      started = true;
      setStatus("processing");
      setRegeneration(({ availableAt }) => ({
        running: true,
        phase: "verifying",
        availableAt,
      }));
      const before = current.spending?.generation ?? 0;
      const rotate = () =>
        onboard(
          current,
          generation,
          true,
          0,
          "rotation",
          (phase) =>
            setRegeneration((state) =>
              state.running ? { ...state, phase } : state
            ),
          // The generation after the newest one the fresh scan shows.
          (latest) =>
            spendingSalt(
              latest.keys.scanKey,
              (latest.spending?.generation ?? 0) + 1
            )
        );
      let rotated: Material | undefined;
      try {
        rotated = await rotate();
      } catch (cause) {
        // Nothing was submitted on a preflight rejection (node lag): wait
        // one block and build again from a fresh scan, once.
        if (!/preflight/i.test(privateErrorMessage(cause))) throw cause;
        await sleep(V2_PREFLIGHT_RETRY_DELAY_MS);
        rotated = await rotate();
      }
      if (!rotated)
        throw new Error("The new Spending account was not confirmed.");
      if (
        generation !== sessionGeneration.current ||
        material.current !== current
      ) {
        wipeKeys(rotated.keys);
        throw new Error(
          "Private Mode changed while creating the new Spending account."
        );
      }
      // Managing an older account's position ends with the switch.
      pinnedRef.current = undefined;
      setPinnedSubwallet(undefined);
      setPinnedPath(undefined);
      publish(rotated, isPrivateModeRef.current);
      rememberEarlierSpending(rotated);
      const created = rotated.spending;
      if (!created || created.generation <= before)
        throw new Error(
          "The new Spending account is not visible yet. Refresh in a moment."
        );
      // Its first funding waits for a short random pause (in memory).
      fundingHold.current = {
        subwallet: created.address,
        notBefore: Date.now() + firstFundingPauseMs(),
      };
    } catch (cause) {
      failure = privateErrorMessage(cause);
      if (started && generation === sessionGeneration.current)
        setStatus(material.current ? "ready" : "error");
      throw new Error(failure);
    } finally {
      regenerating.current = false;
      if (started) {
        busy.current = false;
        regenerateSettledAt.current = Date.now();
      }
      const availableAt = regenerationAvailableAt(
        regenerateSettledAt.current,
        dailyAvailableAt.current
      );
      setRegeneration({
        running: false,
        ...(availableAt !== undefined && { availableAt }),
        ...(failure &&
          generation === sessionGeneration.current && { error: failure }),
      });
    }
  }, [
    assertSessionAccess,
    modelBEnabled,
    onboard,
    publish,
    rememberEarlierSpending,
    services,
    yieldBackground,
  ]);

  // The daily limit for the UI: whenever the current Spending account changes
  // (unlock, a new one, one created on another device), one latest-height
  // read turns the blocks left into a time. Nothing account-specific is read.
  const nextDailyHeight = material.current
    ? nextRegenerateHeight(
        material.current.spending,
        material.current.recovery.notes
      )
    : undefined;
  useEffect(() => {
    let cancelled = false;
    const apply = (at: number | undefined) => {
      if (cancelled) return;
      dailyAvailableAt.current = at;
      const availableAt = regenerationAvailableAt(
        regenerateSettledAt.current,
        at
      );
      setRegeneration((state) =>
        state.availableAt === availableAt
          ? state
          : {
              running: state.running,
              ...(state.phase !== undefined && { phase: state.phase }),
              ...(availableAt !== undefined && { availableAt }),
              ...(state.error !== undefined && { error: state.error }),
            }
      );
    };
    if (nextDailyHeight === undefined) apply(undefined);
    else
      void Promise.resolve()
        .then(() => services().chain.height())
        .then((height) => {
          const left = nextDailyHeight - Number(height);
          apply(left > 0 ? Date.now() + left * blockTimeMs.current : undefined);
        })
        .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [nextDailyHeight, services]);

  const setDustPolicy = useCallback(
    (isDust: (coin: Readonly<V2Coin>) => boolean) => {
      dustPolicy.current = isDust;
    },
    []
  );

  const directDepositMessage = useCallback(
    (value: V2Coin): Msg | null => {
      const current = material.current;
      if (
        !enabled ||
        !selected ||
        !current ||
        status !== "ready" ||
        recoveryExportRequired
      )
        return null;
      const account = {
        address: selected.address.address,
        network: THOR,
      } as const;
      const generation = sessionGeneration.current;
      const identity = onboardingIdentity(current.keys, current.keyContext);
      return new MsgV2DirectExecute(
        account,
        services().deployment.proxyContract,
        async () => {
          assertSessionAccess();
          if (
            generation !== sessionGeneration.current ||
            material.current !== current
          ) {
            throw new Error("Private Mode changed before deposit signing");
          }
          const operationKeys = copyKeys(current.keys);
          try {
            const proofGenerator = await prove();
            if (
              generation !== sessionGeneration.current ||
              material.current !== current
            ) {
              throw new Error("Private Mode changed before deposit signing");
            }
            const prepared = await prepareReserveDeposit(
              {
                keys: operationKeys,
                context: current.keyContext,
                prove: proofGenerator,
              },
              value
            );
            assertSessionAccess();
            if (
              generation !== sessionGeneration.current ||
              material.current !== current
            )
              throw new Error("Private Mode changed before deposit signing");
            return prepared;
          } finally {
            wipeKeys(operationKeys);
          }
        },
        async () => refresh(),
        () => {
          assertSessionAccess();
          const active = material.current;
          if (
            generation !== sessionGeneration.current ||
            !active ||
            onboardingIdentity(active.keys, active.keyContext) !== identity
          )
            throw new Error("Private Mode changed before deposit signing");
        }
      );
    },
    [
      assertSessionAccess,
      enabled,
      prove,
      recoveryExportRequired,
      refresh,
      selected,
      services,
      status,
    ]
  );

  const publicValue = useMemo<V2Session>(() => {
    const current = material.current;
    const activeEntry =
      enabled && current
        ? accountCatalog().get(current.keys.recoveryId)
        : undefined;
    const subwallets = current
      ? activeControlNotes(current)
          .map((note) => (note.note as ControlNote).subwallet)
          .filter((value): value is string => !!value)
      : [];
    const single = privateWalletMode() === "single";
    // Single mode lists the Spending sub first, whichever sub is pinned.
    const spendingScope =
      modelBEnabled && single
        ? interactionSubwallet(current, undefined, positions)
        : undefined;
    const scopes = [
      ...new Set([
        ...(spendingScope ? [spendingScope] : []),
        ...(selectedSubwallet ? [selectedSubwallet] : []),
        ...[...positions].reverse().map((entry) => entry.subwallet),
      ]),
    ];
    return {
      privacy,
      enabled,
      proxyContract: enabled ? services().deployment.proxyContract : undefined,
      status,
      isRefreshing:
        !!current &&
        refreshScope?.generation === sessionGeneration.current &&
        refreshScope?.accountId === current.keys.recoveryId,
      sessionExpired,
      privateSessionLocked: enabled && privateSessionIntent.current && !current,
      sessionPolicy,
      sessionPolicySaved,
      updateSessionPolicy,
      dismissSessionExpired,
      requestSessionUnlock,
      assertSessionAccess,
      error,
      progress,
      operation,
      dismissOperation,
      cancelOperation,
      holdOperationUndo,
      checkOperation,
      recoveryExportRequired,
      // This only recommends creating a package; the catalogue cannot prove
      // that an exported file and its passphrase were saved outside the app.
      backupRecommended:
        status === "ready" &&
        !recoveryExportRequired &&
        !!activeEntry?.walletDerived &&
        !activeEntry.backup,
      subwallets,
      selectedSubwallet,
      reserveBalances: reserveBalances(current),
      accounts: enabled ? accountCatalog().list(walletAddress) : [],
      activeAccountId: current?.keys.recoveryId,
      selectAccount,
      renameAccount,
      removeAccount,
      activateWithWallet,
      continueWalletUnlock,
      cancelWalletUnlock,
      createRandomRecovery,
      confirmRecoveryExport,
      importRecovery,
      exportRecovery,
      onboardCurrent,
      selectSubwallet,
      showPrivate,
      showPublic,
      lock,
      refresh,
      directDepositMessage,
      quotePrivateFees,
      executePrivateIntent,
      isV2DirectMessage: (message): message is MsgV2DirectExecute =>
        message instanceof MsgV2DirectExecute,
      modelB: {
        enabled: modelBEnabled,
        subwallets: subwalletInfos(material.current),
        pinned: pinnedSubwallet,
        pinnedPath,
        positions,
        scopes,
        // Every shown sub (Spending and position subs), each counted once.
        positionsValueUsd: scopes.reduce(
          (sum, subwallet) => sum + (positionValues.get(subwallet) ?? 0n),
          0n
        ),
        reportPositionValue,
        poolReady: subwalletInfos(material.current).some(
          (info) => info.role === "fresh"
        ),
        feeBps,
        subBalances,
        pendingMoves,
        nextSweepAt,
        background,
        lastError: modelBError,
        pin,
        sweepNow,
        setDustPolicy,
        spendingGeneration:
          modelBEnabled &&
          single &&
          current &&
          ["ready", "processing"].includes(status)
            ? (current.spending?.generation ?? 0)
            : undefined,
        regeneration,
        regenerate,
      },
    };
    // revision deliberately republishes summaries when the secret ref changes.
  }, [
    privacy,
    assertSessionAccess,
    dismissSessionExpired,
    requestSessionUnlock,
    sessionExpired,
    sessionPolicy,
    sessionPolicySaved,
    updateSessionPolicy,
    accountCatalog,
    selectAccount,
    renameAccount,
    removeAccount,
    walletAddress,
    activateWithWallet,
    continueWalletUnlock,
    cancelWalletUnlock,
    createRandomRecovery,
    confirmRecoveryExport,
    directDepositMessage,
    dismissOperation,
    cancelOperation,
    holdOperationUndo,
    checkOperation,
    enabled,
    error,
    executePrivateIntent,
    exportRecovery,
    importRecovery,
    lock,
    onboardCurrent,
    operation,
    progress,
    quotePrivateFees,
    refresh,
    refreshScope,
    recoveryExportRequired,
    revision,
    selectSubwallet,
    selectedSubwallet,
    showPrivate,
    showPublic,
    services,
    status,
    modelBEnabled,
    pinnedSubwallet,
    pinnedPath,
    positions,
    positionValues,
    reportPositionValue,
    feeBps,
    subBalances,
    nextSweepAt,
    background,
    modelBError,
    pin,
    sweepNow,
    setDustPolicy,
    regeneration,
    regenerate,
  ]);

  return <Context.Provider value={publicValue}>{children}</Context.Provider>;
};

export const useRedactedV2 = (): V2Session => useContext(Context);
